import { HttpClient, HttpParams } from '@angular/common/http';
import { Component, ElementRef, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { App, URLOpenListenerEvent } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import { Capacitor, PluginListenerHandle } from '@capacitor/core';
import { alertController } from '@ionic/core/components';
import { Subject, debounceTime, distinctUntilChanged, finalize, firstValueFrom, takeUntil } from 'rxjs';
import { createApiUrls } from '@wahl-shared/api-urls';
import { environment } from '../environments/environment';
import { NewsletterEditorComponent } from './newsletter-editor.component';
import { QrCodeFormComponent } from './qr-code-form.component';
import { QrPreviewComponent } from './qr-preview.component';
import { BusinessCardDragEvent } from './business-card-renderer.component';
import {
  AnalyticsLimitMeta,
  AccountContextResponse,
  ApiTokenRecord,
  ApiTokenResponse,
  ApiTokensResponse,
  AuthResponse,
  BusinessCardBackground,
  BusinessCardBackgroundStyle,
  BusinessCardBackgroundStyleId,
  BusinessCardContent,
  BusinessCardContentItem,
  BusinessCardContentInput,
  BusinessCardElementId,
  BusinessCardLayout,
  BusinessCardLayoutInput,
  BusinessCardLayoutPosition,
  BusinessCardLogoSection,
  BusinessCardNfcPayloadMode,
  BusinessCardPayload,
  BusinessCardRecord,
  BusinessCardResponse,
  BusinessCardsResponse,
  BusinessCardLimitMeta,
  BusinessCardSide,
  BusinessCardSideLayout,
  BusinessCardSideLayoutInput,
  BusinessCardTextSection,
  BusinessCardTextSize,
  CategoriesResponse,
  CategoryRecord,
  CompanyContextCompany,
  CurrentUserPreferencesResponse,
  LegalLink,
  LegalPage,
  NewsletterPayload,
  NewsletterRecord,
  NewsletterResponse,
  NewslettersResponse,
  NfcPayloadMode,
  NfcTagLimitMeta,
  NfcTagPayload,
  NfcTagRecord,
  NfcTagResponse,
  NfcTagsResponse,
  OAuthLoginResponse,
  OAuthProviderRecord,
  OAuthProvidersResponse,
  QrCodeRecord,
  QrCodeResponse,
  QrCodeImportResponse,
  QrCodesResponse,
  QrUserAgentRoute,
  QrUserAgentRoutePreset,
  QrStyle,
  QrStyleId,
  StickerSheetAssignmentChange,
  StickerSheetLayout,
  StickerSizeInches,
  ToolMode,
  UrlValidationResponse
} from './app.types';
import { NfcWriterService } from './services/nfc-writer.service';
import { QrRenderService } from './services/qr-render.service';
import { BusinessCardPrintService } from './services/business-card-print.service';
import { AuthSessionService } from './services/auth-session.service';
import { isStaleAuthRequestError } from './services/http-diagnostics.interceptor';
import { QrFormStateService } from './services/qr-form-state.service';

interface TrixConfiguration {
  config?: {
    textAttributes?: Record<string, unknown>;
  };
}

interface BusinessCardTrixEditorElement extends HTMLElement {
  editor?: {
    loadHTML(value: string): void;
  };
  value?: string;
}

interface QrCodeImportPayload {
  title: string;
  target_url: string;
  track_analytics: boolean;
  provided_by_page: boolean;
  qr_style: QrStyleId;
  qr_label_text: string | null;
  qr_icon_size: number;
  qr_label_size: number;
  category_names: string[];
  skip_response_validation: boolean;
}

type OAuthProvider = 'google_oauth2' | 'twitter2' | 'linkedin';

const MAX_STORED_LOGO_DATA_URL_LENGTH = 900_000;
const LOGO_RESIZE_STEPS = [512, 384, 256, 192, 128, 96];
const NEWSLETTER_TEMPLATE_LIMIT = 5;
const OAUTH_APP_CALLBACK_URLS = [
  'qrcodes.wahl.website://auth/callback'
] as const;
const OAUTH_APP_CALLBACK_URL = OAUTH_APP_CALLBACK_URLS[0];
const WEB_OAUTH_CALLBACK_PATH = '/';
const MARKETING_MCP_SERVER_URL = 'https://qrcodes.wahl.website/api/v1/marketing/mcp';
const BUSINESS_CARD_TEXT_SIZE_ATTRIBUTES: Record<Exclude<BusinessCardTextSize, 'normal'>, string> = {
  small: 'businessCardSmall',
  large: 'businessCardLarge',
  xlarge: 'businessCardXLarge'
};
const DEFAULT_BUSINESS_CARD_GRID_SIZE = 4;
const DEFAULT_BUSINESS_CARD_TEXT_COLOR = '#f9fafb';
const SITE_CONTACT_EMAIL = 'jonathan0wahl@gmail.com';
const SITE_CONTACT_PHONE = '(608) 852-9260';
const SUPPORT_LINKS = {
  androidApp: 'https://play.google.com/store/apps/details?id=qrcodes.wahl.website',
  cve: environment.cveSiteUrl,
  feedback: 'https://forms.gle/bCzswv5PxUiGEKtq7',
  googleGroup: 'https://groups.google.com/u/1/g/noisy-user-app-testers/about',
  googlePlayTest: 'https://play.google.com/apps/testing/qrcodes.wahl.website'
};
const DEFAULT_OAUTH_PROVIDERS: Array<{ id: OAuthProvider; label: string }> = [
  { id: 'google_oauth2', label: 'Google' },
  { id: 'twitter2', label: 'X' },
  { id: 'linkedin', label: 'LinkedIn' }
];

interface BillingCheckoutResponse {
  data: {
    url: string;
  };
}

interface BillingConfirmResponse {
  data: {
    payment_level: string;
    status: string;
    plan: string;
  };
}

function configureBusinessCardTrix(): void {
  const trix = (globalThis as typeof globalThis & { Trix?: TrixConfiguration }).Trix;
  const textAttributes = trix?.config?.textAttributes;
  if (!textAttributes) {
    return;
  }

  textAttributes[BUSINESS_CARD_TEXT_SIZE_ATTRIBUTES.small] = {
    style: { fontSize: '0.85em' },
    inheritable: true,
    parser: (element: HTMLElement) => element.style.fontSize === '0.85em'
  };
  textAttributes[BUSINESS_CARD_TEXT_SIZE_ATTRIBUTES.large] = {
    style: { fontSize: '1.2em' },
    inheritable: true,
    parser: (element: HTMLElement) => element.style.fontSize === '1.2em'
  };
  textAttributes[BUSINESS_CARD_TEXT_SIZE_ATTRIBUTES.xlarge] = {
    style: { fontSize: '1.45em' },
    inheritable: true,
    parser: (element: HTMLElement) => element.style.fontSize === '1.45em'
  };
}

@Component({
  selector: 'app-root',
  standalone: false,
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
  encapsulation: ViewEncapsulation.None
})
export class AppComponent implements OnInit, OnDestroy {
  @ViewChild(QrPreviewComponent) private qrPreview?: QrPreviewComponent;
  @ViewChild(QrCodeFormComponent) private qrCodeForm?: QrCodeFormComponent;
  @ViewChild(NewsletterEditorComponent) private newsletterEditor?: NewsletterEditorComponent;
  @ViewChild('editLogoInput') private editLogoInput?: ElementRef<HTMLInputElement>;
  @ViewChild('businessCardPrintSheet') private businessCardPrintSheet?: ElementRef<HTMLElement>;

  private readonly fb = new FormBuilder();
  private readonly apiUrls = createApiUrls(environment);
  private readonly destroy$ = new Subject<void>();
  private readonly newsletterAutosave$ = new Subject<void>();
  private readonly qrCodeSearch$ = new Subject<string>();
  private readonly handleWindowMessage = (event: MessageEvent): void => {
    this.handleOAuthPayload(event.data);
  };
  private readonly handleWindowPopstate = (): void => {
    this.syncLegalPageFromLocation();
  };
  private readonly handleDeviceReady = (): void => {
    this.checkBusinessCardNfcSupport();
  };
  private appUrlOpenListener?: PluginListenerHandle;
  private trixLoadPromise?: Promise<void>;
  private businessCardTrixSyncQueued = false;
  private logoImage: HTMLImageElement | null = null;
  private uploadedLogoDataUrl = '';
  editLogoDataUrl = '';
  authToken = localStorage.getItem('qrRouterToken') || '';
  currentUserSkipProvidedByPage = false;

  readonly form = this.fb.nonNullable.group({
    title: [''],
    targetUrl: ['', [Validators.required]],
    userAgentRoutes: [''],
    userAgentPreset: [''],
    userAgentTargetUrl: [''],
    userAgentRedirectPercentage: [''],
    userAgentEveryNthScan: [''],
    qrLabelText: [''],
    qrIconSize: ['28'],
    qrLabelSize: ['100'],
    qrStyle: ['classic' as QrStyleId],
    categoryNames: [''],
    trackAnalytics: [Boolean(this.authToken)],
    providedByPage: [Boolean(this.authToken)],
    skipResponseValidation: [false]
  });

  readonly editForm = this.fb.nonNullable.group({
    title: [''],
    targetUrl: ['', [Validators.required]],
    userAgentRoutes: [''],
    userAgentPreset: [''],
    userAgentTargetUrl: [''],
    userAgentRedirectPercentage: [''],
    userAgentEveryNthScan: [''],
    qrLabelText: [''],
    qrIconSize: ['28'],
    qrLabelSize: ['100'],
    qrStyle: ['classic' as QrStyleId],
    categoryNames: [''],
    trackAnalytics: [false],
    providedByPage: [false],
    skipResponseValidation: [false]
  });

  readonly businessCardForm = this.fb.nonNullable.group({
    name: ['', [Validators.required]],
    jobTitle: [''],
    company: [''],
    email: [''],
    phone: [''],
    website: [''],
    address: [''],
    tagline: [''],
    categoryNames: ['']
  });

  readonly businessCardQrForm = this.fb.nonNullable.group({
    title: [''],
    targetUrl: [''],
    userAgentRoutes: [''],
    qrLabelText: [''],
    qrIconSize: ['28'],
    qrLabelSize: ['100'],
    qrStyle: ['classic' as QrStyleId],
    categoryNames: [''],
    trackAnalytics: [Boolean(this.authToken)],
    providedByPage: [Boolean(this.authToken)]
  });

  readonly apiTokenForm = this.fb.nonNullable.group({
    expiresAt: [this.defaultApiTokenExpiresAt()],
    neverExpires: [false]
  });

  readonly newsletterForm = this.fb.nonNullable.group({
    title: ['', [Validators.required]],
    categoryNames: ['']
  });

  readonly nfcTagForm = this.fb.nonNullable.group({
    title: ['']
  });

  selectedTool: ToolMode = 'qr';
  private activeQrLabelText = '';
  private activeQrIconSize = 28;
  private activeQrLabelSize = 100;
  private activeQrStickerStyle: QrStyleId = 'classic';
  activeQrStickerSize: StickerSizeInches = 1;
  stickerSheetAssignments: Record<number, number> = {};
  stickerSheetQrCodes: Record<number, QrCodeRecord> = {};
  generatedRecord: QrCodeRecord | null = null;
  editingRecord: QrCodeRecord | null = null;
  generatedBusinessCard: BusinessCardRecord | null = null;
  generatedNewsletter: NewsletterRecord | null = null;
  directQrUrl = '';
  generatedImageUrl = '';
  businessCardQrImageUrl = '';
  businessCardQrImages: Record<number, string> = {};
  businessCardQrCodes: QrCodeRecord[] = [];
  qrCodes: QrCodeRecord[] = [];
  qrCodeSearchQuery = '';
  qrCodePage = 1;
  qrCodePerPage = 10;
  qrCodeTotalCount = 0;
  qrCodeTotalPages = 1;
  categories: CategoryRecord[] = [];
  businessCards: BusinessCardRecord[] = [];
  nfcTags: NfcTagRecord[] = [];
  newsletters: NewsletterRecord[] = [];
  apiTokens: ApiTokenRecord[] = [];
  businessCardLayout: BusinessCardLayout = this.defaultBusinessCardLayout();
  businessCardContent: BusinessCardContent = this.defaultBusinessCardContent();
  selectedBusinessCardSide: BusinessCardSide = 'front';
  businessCardSnapToGrid = true;
  businessCardGridSize = DEFAULT_BUSINESS_CARD_GRID_SIZE;
  readonly businessCardPrintCopies = Array.from({ length: 10 });
  businessCardNfcPayloadMode: BusinessCardNfcPayloadMode = 'url';
  selectedExistingBusinessCardQrCodeId: number | null = null;
  selectedExistingNfcQrCodeId: number | null = null;
  selectedNfcQrCode: QrCodeRecord | null = null;
  showNfcQrCodeSearch = true;
  currentEmail = localStorage.getItem('qrRouterEmail') || '';
  currentUserDoNotTrack = false;
  errorMessage = '';
  qrImportMessage = '';
  qrImportErrors: string[] = [];
  businessCardMessage = '';
  newsletterMessage = '';
  businessCardNfcMessage = '';
  nfcTagMessage = '';
  limitNoticeMessage = '';
  qrCodeLimitBypassed = false;
  urlResponseValidationBypassAllowed = false;
  businessCardLimitMessage = '';
  businessCardLimitBypassed = false;
  nfcTagLimitMessage = '';
  nfcTagLimitBypassed = false;
  authMessage = '';
  apiTokenMessage = '';
  accountContextMessage = '';
  apiTokenValue = '';
  apiTokenValueId: number | null = null;
  apiTokenDateLabels: Record<number, string> = {};
  isSaving = false;
  isImportingQrCodes = false;
  isSavingBusinessCard = false;
  isSavingNewsletter = false;
  isSavingNfcTag = false;
  isLoadingApiTokens = false;
  isCreatingApiToken = false;
  canWriteBusinessCardNfc = false;
  canBroadcastBusinessCardNfc = false;
  canLockBusinessCardNfc = false;
  lockBusinessCardNfcAfterWrite = false;
  isCheckingBusinessCardNfc = false;
  isWritingBusinessCardNfc = false;
  isMassWritingBusinessCardNfc = false;
  massWrittenBusinessCardNfcCount = 0;
  private writeBusinessCardNfcController: AbortController | null = null;
  private massWriteBusinessCardNfcController: AbortController | null = null;
  isBroadcastingBusinessCardNfc = false;
  isStoppingBusinessCardNfcBroadcast = false;
  isLoadingQrCodes = false;
  isLoadingBusinessCards = false;
  isLoadingNfcTags = false;
  isLoadingNewsletters = false;
  isEditModalOpen = false;
  isUserModalOpen = false;
  isUserModalDetailsVisible = false;
  readonly marketingMcpServerUrl = MARKETING_MCP_SERVER_URL;
  editingId: number | null = null;
  deletingId: number | null = null;
  deletingApiTokenId: number | null = null;
  deletingBusinessCardId: number | null = null;
  deletingNfcTagId: number | null = null;
  deletingNewsletterId: number | null = null;
  companies: CompanyContextCompany[] = [];
  currentCompany: CompanyContextCompany | null = null;
  pendingCompanyInvitations: AccountContextResponse['pending_invitations'] = [];
  selectedCompanyContext = 'individual';
  newCompanyName = '';
  inviteEmail = '';
  inviteRole: CompanyContextCompany['role'] = 'member';
  isLoadingAccountContext = false;
  isSavingAccountContext = false;
  isCreatingCompany = false;
  isInvitingCompanyMember = false;
  selectedNfcTag: NfcTagRecord | null = null;
  private newsletterAutosavePending = false;
  legalExpanded = false;
  selectedLegalPage: LegalPage | null = null;
  private activeBusinessCardDrag: {
    elementId: BusinessCardElementId;
    side: BusinessCardSide;
    pointerId: number;
    startClientX: number;
    startClientY: number;
    originX: number;
    originY: number;
    cardWidth: number;
    cardHeight: number;
  } | null = null;

  readonly qrStyles: QrStyle[] = [
    { id: 'classic', label: 'Classic', dark: '#111827', light: '#ffffff', shape: 'square' },
    { id: 'rounded', label: 'Rounded', dark: '#111827', light: '#ffffff', shape: 'rounded' },
    { id: 'dots', label: 'Dots', dark: '#111827', light: '#ffffff', shape: 'dot' },
    { id: 'ocean', label: 'Ocean', dark: '#0f766e', accent: '#2563eb', light: '#f8fafc', shape: 'rounded' },
    { id: 'sunset', label: 'Sunset', dark: '#be123c', accent: '#f97316', light: '#fff7ed', shape: 'dot' },
    { id: 'forest', label: 'Forest', dark: '#166534', accent: '#65a30d', light: '#f7fee7', shape: 'rounded' },
    { id: 'midnight', label: 'Midnight', dark: '#312e81', accent: '#0891b2', light: '#eef2ff', shape: 'diamond' },
    { id: 'berry', label: 'Berry', dark: '#86198f', accent: '#e11d48', light: '#fdf4ff', shape: 'plus' },
    { id: 'highContrast', label: 'High Contrast', dark: '#000000', light: '#ffffff', shape: 'square' },
    { id: 'logoWatermark', label: 'Logo Watermark', dark: '#111827', light: '#ffffff', shape: 'rounded', logoBackground: 'watermark' },
    { id: 'logoPoster', label: 'Logo Poster', dark: '#ffffff', light: '#111827', shape: 'dot', logoBackground: 'poster' }
  ];
  readonly marketingEmailEnabled = environment.enableMarketingEmail;
  readonly canShowNfcTool = environment.enableNfc && this.isAndroidMobile();
  readonly userAgentRoutePresets = new QrFormStateService().userAgentRoutePresets;
  readonly businessCardBackgroundStyles: BusinessCardBackgroundStyle[] = [
    { id: 'linear', label: 'Soft Fade' },
    { id: 'aurora', label: 'Aurora' },
    { id: 'storm', label: 'Storm' },
    { id: 'sunrise', label: 'Sunrise' },
    { id: 'spotlight', label: 'Spotlight' }
  ];

  readonly supportLinks = SUPPORT_LINKS;
  oauthProviders: Array<{ id: OAuthProvider; label: string }> = [];
  readonly swaggerUrl = this.apiUrl('/api-docs');
  readonly modalBreakpoints = [0, 1];
  readonly defaultCategoryNames = [
    'Website',
    'Deep Link',
    'Menu',
    'Booking',
    'Portfolio',
    'Storefront',
    'Event',
    'Spring',
    'Email',
    'Retail',
    'Trade Show',
    'Postcards'
  ];
  readonly legalPages: LegalPage[] = [
    {
      slug: 'privacy',
      title: 'Privacy Policy',
      updated: 'April 22, 2026',
      sections: [
        {
          heading: 'What We Collect',
          paragraphs: [
            'Wahl Advertising stores the account email returned by Google sign-in, QR code records you create, destination URLs, optional QR logos, and scan counts for tracked QR codes.',
            'When tracked analytics are enabled, the service may record scan time, IP address, user agent, and referrer for each scan.'
          ]
        },
        {
          heading: 'How We Use Information',
          paragraphs: [
            'We use this information to authenticate you, generate QR codes, route tracked scans, show your QR code list, and provide basic scan analytics.',
            'We do not sell personal information.'
          ]
        },
        {
          heading: 'Your Choices',
          paragraphs: [
            'You can create direct QR codes without analytics. Direct QR codes encode the destination website directly and do not create a tracked redirect record.',
            'You can delete saved QR codes from your account at any time.'
          ]
        }
      ]
    },
    {
      slug: 'terms',
      title: 'Terms & Conditions',
      updated: 'April 22, 2026',
      sections: [
        {
          heading: 'Use of the Service',
          paragraphs: [
            'You are responsible for the URLs, titles, and logos you create with Wahl Advertising.',
            'Do not use the service for unlawful, deceptive, abusive, or harmful destinations.'
          ]
        },
        {
          heading: 'Availability',
          paragraphs: [
            'QR routing and analytics are provided as-is. Redirect availability can depend on hosting, networking, and the destination website.'
          ]
        },
        {
          heading: 'Account Access',
          paragraphs: [
            'Google sign-in is used for account access. Keep your Google account secure and sign out on shared devices.'
          ]
        }
      ]
    },
    {
      slug: 'cookies',
      title: 'Cookie Policy',
      updated: 'April 22, 2026',
      sections: [
        {
          heading: 'Local Storage',
          paragraphs: [
            'Wahl Advertising uses local storage to remember your sign-in token, email, and cookie preference on this device.',
            'Local storage is necessary for the current browser session experience.'
          ]
        },
        {
          heading: 'Optional Cookies',
          paragraphs: [
            'This app does not currently use optional analytics cookies. If optional cookies are added later, the Cookie Preferences control will be used to manage consent.'
          ]
        }
      ]
    },
    {
      slug: 'dmca',
      title: 'DMCA Notice',
      updated: 'April 22, 2026',
      sections: [
        {
          heading: 'Copyright Complaints',
          paragraphs: [
            `If you believe content available through a QR code infringes your copyright, send a notice to ${SITE_CONTACT_EMAIL} or ${SITE_CONTACT_PHONE} with the QR redirect path, destination URL, your contact information, and a description of the copyrighted work.`,
            'Wahl Advertising can remove tracked QR records that violate applicable law or these terms.'
          ]
        },
        {
          heading: 'Content Credits',
          paragraphs: [
            'User-uploaded logos, images, and destination content remain the responsibility of the uploading user. Wahl Advertising does not provide bundled media credits for user-created QR codes.'
          ]
        }
      ]
    },
    {
      slug: 'accessibility',
      title: 'Accessibility Statement',
      updated: 'April 22, 2026',
      sections: [
        {
          heading: 'Commitment',
          paragraphs: [
            'Wahl Advertising aims to provide a usable interface with semantic controls, keyboard-accessible actions, and readable contrast.',
            'If you find an accessibility issue, use the Feedback link and include the page, browser, assistive technology, and steps to reproduce.'
          ]
        }
      ]
    },
    {
      slug: 'security',
      title: 'Security',
      updated: 'April 22, 2026',
      sections: [
        {
          heading: 'Reporting Issues',
          paragraphs: [
            'Please report suspected security issues through the Feedback link. Include enough detail to reproduce the issue, but do not access data that is not yours.',
            'Wahl Advertising validates QR destinations as HTTP or HTTPS URLs and scopes saved QR code management to the signed-in owner.'
          ]
        }
      ]
    },
    {
      slug: 'contact',
      title: 'Contact',
      updated: 'April 22, 2026',
      sections: [
        {
          heading: 'Support',
          paragraphs: [
            `For support requests, bug reports, legal notices, and feature requests, contact ${SITE_CONTACT_EMAIL} or ${SITE_CONTACT_PHONE}.`,
            'The Google Group link is available for announcements and community discussion.'
          ]
        }
      ]
    }
  ];
  readonly legalLinks: LegalLink[] = [
    { label: 'Privacy', slug: 'privacy' },
    { label: 'Terms', slug: 'terms' },
    { label: 'Cookies', slug: 'cookies' },
    { label: 'DMCA', slug: 'dmca' },
    { label: 'Content Credits', slug: 'dmca', fragment: 'credits' },
    { label: 'Accessibility', slug: 'accessibility' },
    { label: 'Security', slug: 'security' },
    { label: 'Contact', slug: 'contact' }
  ];

  ngOnInit(): void {
    this.syncLegalPageFromLocation();
    this.syncTrackedAnalyticsControl();
    this.syncBusinessCardTrackedAnalyticsControl();
    this.syncApiTokenExpiresControl();
    this.loadOAuthProviders();
    this.registerAppUrlOpenListener();
    this.handleOAuthCallbackUrl(window.location.href);
    this.handleBillingReturn(window.location.href);

    if (this.authToken) {
      this.loadAccountContext();
      this.loadQrCodes();
      this.loadCurrentUserPreferences();
      this.loadCategories();
    }

    this.checkBusinessCardNfcSupport();
    document.addEventListener('deviceready', this.handleDeviceReady, { once: true });
    this.newsletterAutosave$
      .pipe(debounceTime(1800), takeUntil(this.destroy$))
      .subscribe(() => {
        void this.autosaveNewsletter();
      });
    this.qrCodeSearch$
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(() => {
        this.loadQrCodes(1);
      });
    this.apiTokenForm.controls.neverExpires.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.syncApiTokenExpiresControl();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    document.removeEventListener('deviceready', this.handleDeviceReady);
    window.removeEventListener('message', this.handleWindowMessage);
    window.removeEventListener('popstate', this.handleWindowPopstate);
    void this.appUrlOpenListener?.remove();
    void this.nfcWriter.stopBroadcast();
  }

  onSelectedToolChange(value: unknown): void {
    const selectedTool = this.validToolMode(value);
    this.selectedTool = selectedTool;

    if (selectedTool === 'businessCard') {
      void this.ensureBusinessCardEditorLoaded().then(() => this.scheduleBusinessCardTrixSync());
      this.loadDefaultQrCodePickerResults();
      if (this.authToken && this.businessCards.length === 0 && !this.isLoadingBusinessCards) {
        this.loadBusinessCards();
      }
      return;
    }

    if (selectedTool === 'stickers') {
      this.loadDefaultQrCodePickerResults();
      return;
    }

    if (selectedTool === 'nfc') {
      this.loadDefaultQrCodePickerResults();
      if (this.authToken && this.nfcTags.length === 0 && !this.isLoadingNfcTags) {
        this.loadNfcTags();
      }
      return;
    }
  }

  async onLogoSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];

    if (!file) {
      this.logoImage = null;
      this.uploadedLogoDataUrl = '';
      return;
    }

    try {
      this.uploadedLogoDataUrl = await this.compressLogoDataUrl(file);
      await this.setActiveLogo(this.uploadedLogoDataUrl);
      this.errorMessage = '';
    } catch {
      this.logoImage = null;
      this.uploadedLogoDataUrl = '';
      this.errorMessage = 'Unable to process that logo. Try a smaller image file.';
      return;
    }

  }

  async onEditLogoSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];

    if (!file) {
      this.editLogoDataUrl = this.editingRecord?.logo_data_url || '';
      return;
    }

    try {
      this.editLogoDataUrl = await this.compressLogoDataUrl(file);
      this.errorMessage = '';
    } catch {
      this.editLogoDataUrl = this.editingRecord?.logo_data_url || '';
      this.errorMessage = 'Unable to process that logo. Try a smaller image file.';
    }
  }

  async removeEditQrLogo(): Promise<void> {
    this.editLogoDataUrl = '';
    this.errorMessage = '';
    if (this.editLogoInput?.nativeElement) {
      this.editLogoInput.nativeElement.value = '';
    }

    await this.setActiveLogo(null);
    if (this.editingRecord) {
      const value = this.editForm.getRawValue();
      await this.renderQrPreview(
        this.redirectUrlFor(this.editingRecord),
        value.qrStyle,
        value.qrLabelText || '',
        this.normalizedQrIconSize(value.qrIconSize),
        this.normalizedQrLabelSize(value.qrLabelSize)
      );
    }
  }

  async redrawCurrentQrCode(): Promise<void> {
    if (this.generatedRecord) {
      await this.renderQrPreview(
        this.redirectUrlFor(this.generatedRecord),
        this.generatedRecord.qr_style,
        this.generatedRecord.qr_label_text || '',
        this.generatedRecord.qr_icon_size,
        this.generatedRecord.qr_label_size
      );
    } else if (this.directQrUrl) {
      await this.renderQrPreview(this.directQrUrl, this.activeQrStickerStyle, this.activeQrLabelText, this.activeQrIconSize, this.activeQrLabelSize);
    }
  }

  async createQrCode(): Promise<void> {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.isSaving = true;
    this.errorMessage = '';
    this.generatedRecord = null;
    this.directQrUrl = '';
    await this.setActiveLogo(this.uploadedLogoDataUrl || null);

    const value = this.form.getRawValue();
    this.activeQrLabelText = value.qrLabelText.trim();
    this.activeQrIconSize = this.normalizedQrIconSize(value.qrIconSize);
    this.activeQrLabelSize = this.normalizedQrLabelSize(value.qrLabelSize);
    this.activeQrStickerStyle = value.qrStyle;
    let validatedUrl: string;
    let userAgentRoutes: QrUserAgentRoute[] = [];

    try {
      validatedUrl = await this.validateQrDestinationUrl(value.targetUrl, value.skipResponseValidation);
      if (value.trackAnalytics) {
        userAgentRoutes = await this.validateUserAgentRoutes(
          this.userAgentRoutesInputForSubmit(this.form),
          value.skipResponseValidation
        );
      }
    } catch (error) {
      this.errorMessage = this.validationErrorMessage(error);
      this.isSaving = false;
      return;
    }

    if (!value.trackAnalytics) {
      this.generateDirectQr(validatedUrl);
      return;
    }

    this.http.post<QrCodeResponse>(this.apiV1Url('qr_codes'), {
      qr_code: {
        title: value.title,
        target_url: validatedUrl,
        track_analytics: true,
        provided_by_page: value.providedByPage,
        qr_style: value.qrStyle,
        qr_label_text: value.qrLabelText || null,
        qr_icon_size: this.activeQrIconSize,
        qr_label_size: this.activeQrLabelSize,
        logo_data_url: this.uploadedLogoDataUrl || null,
        user_agent_routes: userAgentRoutes,
        category_names: this.categoryNamesFromInput(value.categoryNames),
        skip_response_validation: value.skipResponseValidation
      }
    }, { headers: this.authHeaders() }).subscribe({
      next: async (response) => {
        this.generatedRecord = response.data;
        this.upsertQrCode(response.data);
        this.mergeCategoryNames(response.data.category_names);
        this.syncLimitNotice(response.meta);
        this.activeQrLabelText = response.data.qr_label_text || '';
        this.activeQrIconSize = response.data.qr_icon_size;
        this.activeQrLabelSize = response.data.qr_label_size;
        this.activeQrStickerStyle = response.data.qr_style;
        await this.renderQrRecordPreview(response.data);
        this.resetFormForReuse();
        this.isSaving = false;
      },
      error: (error) => {
        if (this.handleAuthError(error)) {
          this.isSaving = false;
          return;
        }

        this.syncLimitNotice(error.error?.meta);
        this.errorMessage = this.apiErrorMessage(error, 'Unable to create QR code.');
        this.isSaving = false;
      }
    });
  }

  importQrCodes(importText: string): void {
    if (!this.authToken || this.isImportingQrCodes) {
      this.qrImportErrors = ['Sign in with Google to import QR codes.'];
      return;
    }

    const qrCodes = this.qrImportPayloadsFromText(importText);
    if (qrCodes.length === 0) {
      this.qrImportErrors = ['Add at least one row with a QR destination.'];
      this.qrImportMessage = '';
      return;
    }

    this.isImportingQrCodes = true;
    this.qrImportMessage = '';
    this.qrImportErrors = [];

    this.http.post<QrCodeImportResponse>(this.apiV1Url('qr_codes/imports'), {
      qr_codes: qrCodes
    }, { headers: this.authHeaders() }).subscribe({
      next: (response) => {
        response.data.forEach((record) => {
          this.upsertQrCode(record);
          this.mergeCategoryNames(record.category_names);
        });
        this.syncLimitNotice(response.meta);
        this.qrImportErrors = response.errors.map((error) => (
          `Row ${error.row}: ${error.errors.join(', ')}`
        ));
        const importedCount = response.meta?.imported_count ?? response.data.length;
        const failedCount = response.meta?.failed_count ?? response.errors.length;
        this.qrImportMessage = failedCount > 0
          ? `Imported ${importedCount} QR codes; ${failedCount} rows need attention.`
          : `Imported ${importedCount} QR codes.`;
        this.loadQrCodes(1);
        this.isImportingQrCodes = false;
      },
      error: (error) => {
        if (this.handleAuthError(error)) {
          this.isImportingQrCodes = false;
          return;
        }

        this.syncLimitNotice(error.error?.meta);
        this.qrImportErrors = [this.apiErrorMessage(error, 'Unable to import QR codes.')];
        this.qrImportMessage = '';
        this.isImportingQrCodes = false;
      }
    });
  }

  importQrCodesFromCsv(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';

    if (!file) {
      return;
    }

    file.text()
      .then((content) => this.importQrCodes(content))
      .catch(() => {
        this.qrImportErrors = ['Unable to read the selected CSV file.'];
      });
  }

  downloadQrImportTemplate(): void {
    const csv = [
      'title,target_url,category_names',
      'Storefront,https://example.com,Website',
      'Menu,https://example.com/menu,"Restaurant, Menu"'
    ].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const anchor = document.createElement('a');

    anchor.href = URL.createObjectURL(blob);
    anchor.download = 'qr-code-import-template.csv';
    anchor.click();
    URL.revokeObjectURL(anchor.href);
  }

  async startGoogleOAuth(): Promise<void> {
    await this.startOAuth('google_oauth2');
  }

  async startOAuth(provider: OAuthProvider): Promise<void> {
    if (this.shouldUseNativeOAuth()) {
      await this.startNativeOAuth(provider);
      return;
    }

    const authUrl = await this.oauthLoginUrl(
      new HttpParams()
        .set('provider', provider)
        .set('origin', this.webOAuthCallbackUrl())
    );
    window.open(authUrl.toString(), `${provider}-oauth`, 'width=520,height=720');
  }

  loadOAuthProviders(): void {
    this.http.get<OAuthProvidersResponse>(this.apiV1Url('oauth_providers'))
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.oauthProviders = this.availableOAuthProviders(response.data);
        },
        error: () => {
          this.oauthProviders = [];
        }
      });
  }

  toggleLegal(): void {
    this.legalExpanded = !this.legalExpanded;
  }

  openLegalPage(link: LegalLink, event?: Event): void {
    event?.preventDefault();

    const page = this.legalPages.find((legalPage) => legalPage.slug === link.slug);
    if (!page) {
      return;
    }

    this.selectedLegalPage = page;
    this.legalExpanded = true;
    window.history.pushState({ legalPage: link.slug }, '', `/${link.slug}${link.fragment ? `#${link.fragment}` : ''}`);
  }

  closeLegalPage(): void {
    this.selectedLegalPage = null;
    window.history.pushState({}, '', '/');
    setTimeout(() => {
      void this.redrawCurrentQrCode();
    });
  }

  async openCookiePreferences(): Promise<void> {
    const alert = await alertController.create({
      header: 'Cookie Preferences',
      message: 'We use necessary local storage to run this QR app. Optional analytics cookies are not used here unless added later.',
      buttons: [
        {
          text: 'Reject non-essential',
          role: 'reject',
          handler: () => this.session.saveCookiePreference('necessary')
        },
        {
          text: 'Accept all',
          role: 'accept',
          handler: () => this.session.saveCookiePreference('all')
        },
        {
          text: 'Close',
          role: 'cancel'
        }
      ]
    });

    await alert.present();
  }

  logout(): void {
    this.isUserModalOpen = false;
    this.clearAuthSession('Signed out.');
  }

  openBilling(event?: Event): void {
    event?.preventDefault();

    if (!this.authToken) {
      this.authMessage = 'Sign in with Google to subscribe for unlimited use.';
      return;
    }

    this.http.post<BillingCheckoutResponse>(
      this.apiV1Url('billing/checkout'),
      { return_url: window.location.href },
      { headers: this.authHeaders() }
    ).subscribe({
      next: (response) => {
        window.location.href = response.data.url;
      },
      error: (error) => {
        this.authMessage = this.apiErrorMessage(error, 'Unable to start Stripe checkout.');
      }
    });
  }

  private handleBillingReturn(url: string): void {
    let callbackUrl: URL;

    try {
      callbackUrl = new URL(url);
    } catch {
      return;
    }

    const billingStatus = callbackUrl.searchParams.get('billing');
    const sessionId = callbackUrl.searchParams.get('stripe_checkout_session_id');
    if (billingStatus === 'cancel') {
      this.authMessage = 'Stripe checkout was canceled.';
      this.clearBillingReturnParams(callbackUrl);
      return;
    }

    if (billingStatus !== 'success' || !sessionId) {
      return;
    }

    if (!this.authToken) {
      this.authMessage = 'Sign in again to confirm your Stripe subscription.';
      return;
    }

    this.http.post<BillingConfirmResponse>(
      this.apiV1Url('billing/checkout/confirm'),
      { session_id: sessionId },
      { headers: this.authHeaders() }
    ).subscribe({
      next: (response) => {
        this.authMessage = response.data.payment_level === 'unlimited'
          ? 'Stripe subscription confirmed. Unlimited use is active.'
          : 'Stripe checkout is still processing.';
        this.loadAccountContext();
        this.reloadAuthenticatedData();
        this.clearBillingReturnParams(callbackUrl);
      },
      error: (error) => {
        this.authMessage = this.apiErrorMessage(error, 'Unable to confirm Stripe checkout.');
      }
    });
  }

  private clearBillingReturnParams(callbackUrl: URL): void {
    callbackUrl.searchParams.delete('billing');
    callbackUrl.searchParams.delete('stripe_checkout_session_id');
    window.history.replaceState(window.history.state, document.title, callbackUrl.toString());
  }

  openUserModal(): void {
    if (!this.authToken) {
      return;
    }

    this.isUserModalDetailsVisible = true;
    this.isUserModalOpen = true;
    this.loadAccountContext();
    this.loadApiTokens();
  }

  closeUserModal(): void {
    this.isUserModalOpen = false;
    this.isUserModalDetailsVisible = false;
  }

  get canInviteCompanyMembers(): boolean {
    return Boolean(this.currentCompany?.permissions?.can_invite_company_members);
  }

  get currentAccountLabel(): string {
    return this.currentCompany ? this.currentCompany.name : 'Individual';
  }

  loadAccountContext(): void {
    if (!this.authToken || this.isLoadingAccountContext) {
      return;
    }

    this.isLoadingAccountContext = true;
    this.http.get<AccountContextResponse>(this.apiUrl('/api/users/account_context'), {
      headers: this.authHeaders()
    }).subscribe({
      next: (response) => {
        this.applyAccountContext(response);
        this.isLoadingAccountContext = false;
      },
      error: (error) => {
        if (this.handleAuthError(error)) {
          this.isLoadingAccountContext = false;
          return;
        }

        this.accountContextMessage = this.apiErrorMessage(error, 'Unable to load account context.');
        this.isLoadingAccountContext = false;
      }
    });
  }

  switchCompanyContext(value: string | number | null | undefined): void {
    if (!this.authToken || this.isSavingAccountContext) {
      return;
    }

    const currentCompanyId = value === 'individual' || value === null || value === undefined ? null : Number(value);
    this.isSavingAccountContext = true;
    this.http.patch<AccountContextResponse>(
      this.apiUrl('/api/users/account_context'),
      { account_context: { current_company_id: currentCompanyId } },
      { headers: this.authHeaders() }
    ).subscribe({
      next: (response) => {
        this.applyAccountContext(response);
        this.reloadAuthenticatedData();
        this.accountContextMessage = `Using ${this.currentAccountLabel}.`;
        this.isSavingAccountContext = false;
      },
      error: (error) => {
        if (this.handleAuthError(error)) {
          this.isSavingAccountContext = false;
          return;
        }

        this.accountContextMessage = this.apiErrorMessage(error, 'Unable to switch account context.');
        this.isSavingAccountContext = false;
      }
    });
  }

  createCompany(): void {
    const name = this.newCompanyName.trim();
    if (!this.authToken || !name || this.isCreatingCompany) {
      return;
    }

    this.isCreatingCompany = true;
    this.http.post<AccountContextResponse>(
      this.apiUrl('/api/users/companies'),
      { company: { name } },
      { headers: this.authHeaders() }
    ).subscribe({
      next: (response) => {
        this.newCompanyName = '';
        this.applyAccountContext(response);
        this.reloadAuthenticatedData();
        this.accountContextMessage = `Created ${this.currentAccountLabel}.`;
        this.isCreatingCompany = false;
      },
      error: (error) => {
        if (this.handleAuthError(error)) {
          this.isCreatingCompany = false;
          return;
        }

        this.accountContextMessage = this.apiErrorMessage(error, 'Unable to create company.');
        this.isCreatingCompany = false;
      }
    });
  }

  inviteCompanyMember(): void {
    const email = this.inviteEmail.trim();
    if (!this.authToken || !this.currentCompany || !email || this.isInvitingCompanyMember) {
      return;
    }

    this.isInvitingCompanyMember = true;
    this.http.post(
      this.apiUrl(`/api/users/companies/${this.currentCompany.id}/invitations`),
      { invitation: { email, role: this.inviteRole } },
      { headers: this.authHeaders() }
    ).subscribe({
      next: () => {
        this.inviteEmail = '';
        this.accountContextMessage = 'Invitation created.';
        this.isInvitingCompanyMember = false;
      },
      error: (error) => {
        if (this.handleAuthError(error)) {
          this.isInvitingCompanyMember = false;
          return;
        }

        this.accountContextMessage = this.apiErrorMessage(error, 'Unable to invite company member.');
        this.isInvitingCompanyMember = false;
      }
    });
  }

  acceptCompanyInvitation(token: string): void {
    if (!this.authToken || !token) {
      return;
    }

    this.http.post<AccountContextResponse>(this.apiUrl(`/api/users/company_invitations/${token}/accept`), {}, {
      headers: this.authHeaders()
    }).subscribe({
      next: (response) => {
        this.applyAccountContext(response);
        this.reloadAuthenticatedData();
        this.accountContextMessage = `Using ${this.currentAccountLabel}.`;
      },
      error: (error) => {
        if (this.handleAuthError(error)) {
          return;
        }

        this.accountContextMessage = this.apiErrorMessage(error, 'Unable to accept invitation.');
      }
    });
  }

  private applyAccountContext(response: AccountContextResponse): void {
    this.companies = response.companies || [];
    this.currentCompany = response.current_company;
    this.pendingCompanyInvitations = response.pending_invitations || [];
    this.selectedCompanyContext = this.currentCompany ? String(this.currentCompany.id) : 'individual';
  }

  private reloadAuthenticatedData(): void {
    this.qrCodes = [];
    this.businessCards = [];
    this.nfcTags = [];
    this.newsletters = [];
    this.categories = [];
    this.loadQrCodes();
    this.loadCategories();
    this.onSelectedToolChange(this.selectedTool);
  }

  private clearAuthSession(message: string): void {
    this.authToken = '';
    this.currentEmail = '';
    this.qrCodes = [];
    this.stickerSheetAssignments = {};
    this.stickerSheetQrCodes = {};
    this.categories = [];
    this.businessCards = [];
    this.newsletters = [];
    this.nfcTags = [];
    this.selectedNfcTag = null;
    this.generatedNewsletter = null;
    this.apiTokens = [];
    this.companies = [];
    this.currentCompany = null;
    this.pendingCompanyInvitations = [];
    this.selectedCompanyContext = 'individual';
    this.newCompanyName = '';
    this.inviteEmail = '';
    this.accountContextMessage = '';
    this.apiTokenValue = '';
    this.apiTokenValueId = null;
    this.apiTokenMessage = '';
    this.apiTokenDateLabels = {};
    this.limitNoticeMessage = '';
    this.qrCodeLimitBypassed = false;
    this.urlResponseValidationBypassAllowed = false;
    this.businessCardLimitMessage = '';
    this.nfcTagLimitMessage = '';
    this.nfcTagMessage = '';
    this.currentUserDoNotTrack = false;
    this.currentUserSkipProvidedByPage = false;
    this.businessCardLimitBypassed = false;
    this.nfcTagLimitBypassed = false;
    this.isLoadingQrCodes = false;
    if (this.selectedTool === 'marketingEmail' || this.selectedTool === 'stickers') {
      this.selectedTool = 'qr';
    }
    this.syncTrackedAnalyticsControl();
    this.syncBusinessCardTrackedAnalyticsControl();
    this.session.clear();
    this.authMessage = message;
  }

  private handleAuthError(error: unknown): boolean {
    if (!this.isAuthFailureResponse(error)) {
      return false;
    }

    if (isStaleAuthRequestError(error)) {
      console.info('[Wahl Auth] Ignored stale authenticated request failure');
      return true;
    }

    this.clearAuthSession('Unable to authenticate. Sign in with Google to continue.');
    return true;
  }

  private isAuthFailureResponse(error: unknown): boolean {
    if (!error || typeof error !== 'object') {
      return false;
    }

    if ('status' in error && Number((error as { status?: unknown }).status) === 401) {
      return true;
    }

    const responseError = 'error' in error ? (error as { error?: unknown }).error : null;
    if (!responseError || typeof responseError !== 'object') {
      return false;
    }

    const message = 'error' in responseError && typeof responseError.error === 'string'
      ? responseError.error
      : '';

    return message.toLowerCase().includes('unable to authenticate');
  }

  loadQrCodes(page = this.qrCodePage): void {
    if (!this.authToken) {
      return;
    }

    const requestedPage = Math.max(1, page);
    const params = new HttpParams()
      .set('page', String(requestedPage))
      .set('per_page', String(this.qrCodePerPage))
      .set('q', this.qrCodeSearchQuery.trim());

    this.isLoadingQrCodes = true;
    this.http.get<QrCodesResponse>(
      this.apiV1Url('qr_codes'),
      { headers: this.authHeaders(), params }
    ).pipe(
      finalize(() => {
        this.isLoadingQrCodes = false;
      })
    ).subscribe({
      next: (response) => {
        this.qrCodes = response.data;
        this.qrCodePage = response.meta?.page || requestedPage;
        this.qrCodeTotalCount = response.meta?.total_count || response.data.length;
        this.qrCodeTotalPages = Math.max(1, response.meta?.total_pages || 1);
        response.data.forEach((record) => this.mergeCategoryNames(record.category_names));
        this.syncLimitNotice(response.meta);
      },
      error: (error) => {
        if (this.handleAuthError(error)) {
          return;
        }

        this.errorMessage = this.apiErrorMessage(error, 'Unable to load your QR codes.');
      }
    });
  }

  onQrCodeSearchChange(query: string): void {
    this.qrCodeSearchQuery = query;
    this.qrCodeSearch$.next(query);
  }

  private loadDefaultQrCodePickerResults(): void {
    if (!this.authToken || this.isLoadingQrCodes) {
      return;
    }

    if (this.qrCodeSearchQuery.trim() || this.qrCodes.length === 0) {
      this.qrCodeSearchQuery = '';
      this.loadQrCodes(1);
    }
  }

  loadQrCodePage(page: number): void {
    if (page < 1 || page > this.qrCodeTotalPages || page === this.qrCodePage) {
      return;
    }

    this.loadQrCodes(page);
  }

  loadCategories(): void {
    if (!this.authToken) {
      return;
    }

    this.http.get<CategoriesResponse>(
      this.apiV1Url('categories'),
      { headers: this.authHeaders() }
    ).subscribe({
      next: (response) => {
        this.categories = response.data;
      },
      error: (error) => {
        if (this.handleAuthError(error)) {
          return;
        }

        this.errorMessage = this.apiErrorMessage(error, 'Unable to load categories.');
      }
    });
  }

  loadBusinessCards(): void {
    if (!this.authToken) {
      return;
    }

    this.isLoadingBusinessCards = true;
    this.http.get<BusinessCardsResponse>(
      this.apiV1Url('business_cards'),
      { headers: this.authHeaders() }
    ).subscribe({
      next: (response) => {
        this.businessCards = response.data;
        response.data.forEach((record) => this.mergeCategoryNames(record.category_names));
        this.syncBusinessCardLimitNotice(response.meta);
        this.isLoadingBusinessCards = false;
      },
      error: (error) => {
        if (this.handleAuthError(error)) {
          this.isLoadingBusinessCards = false;
          return;
        }

        this.businessCardMessage = this.apiErrorMessage(error, 'Unable to load your business cards.');
        this.isLoadingBusinessCards = false;
      }
    });
  }

  loadNfcTags(): void {
    if (!this.authToken) {
      return;
    }

    this.isLoadingNfcTags = true;
    this.http.get<NfcTagsResponse>(
      this.apiV1Url('nfc_tags'),
      { headers: this.authHeaders() }
    ).subscribe({
      next: (response) => {
        this.nfcTags = response.data;
        this.syncNfcTagLimitNotice(response.meta);
        this.isLoadingNfcTags = false;
      },
      error: (error) => {
        if (this.handleAuthError(error)) {
          this.isLoadingNfcTags = false;
          return;
        }

        this.nfcTagMessage = this.apiErrorMessage(error, 'Unable to load your NFC tags.');
        this.isLoadingNfcTags = false;
      }
    });
  }

  loadNewsletters(): void {
    if (!this.authToken) {
      return;
    }

    this.isLoadingNewsletters = true;
    this.http.get<NewslettersResponse>(
      this.apiV1Url('newsletters'),
      { headers: this.authHeaders() }
    ).subscribe({
      next: (response) => {
        this.newsletters = response.data;
        response.data.forEach((record) => this.mergeCategoryNames(record.category_names));
        this.isLoadingNewsletters = false;
      },
      error: (error) => {
        if (this.handleAuthError(error)) {
          this.isLoadingNewsletters = false;
          return;
        }

        this.newsletterMessage = this.apiErrorMessage(error, 'Unable to load your news letters.');
        this.isLoadingNewsletters = false;
      }
    });
  }

  loadCurrentUserPreferences(): void {
    if (!this.authToken) {
      return;
    }

    this.http.get<CurrentUserPreferencesResponse>(
      this.apiV1Url('current_user_preferences'),
      { headers: this.authHeaders() }
    ).subscribe({
      next: (response) => {
        this.currentUserDoNotTrack = response.data.do_not_track;
        this.currentUserSkipProvidedByPage = Boolean(response.data.skip_provided_by_page);
        this.applyProvidedByPreferenceToNewQrForms();
      },
      error: (error) => {
        if (this.handleAuthError(error)) {
          return;
        }

        this.authMessage = this.apiErrorMessage(error, 'Unable to load your preferences.');
      }
    });
  }

  updateCurrentUserDoNotTrack(value: boolean): void {
    if (!this.authToken) {
      return;
    }

    this.currentUserDoNotTrack = value;
    this.http.patch<CurrentUserPreferencesResponse>(
      this.apiV1Url('current_user_preferences'),
      { user: { do_not_track: value } },
      { headers: this.authHeaders() }
    ).subscribe({
      next: (response) => {
        this.currentUserDoNotTrack = response.data.do_not_track;
        this.currentUserSkipProvidedByPage = Boolean(response.data.skip_provided_by_page);
      },
      error: (error) => {
        if (this.handleAuthError(error)) {
          return;
        }

        this.authMessage = this.apiErrorMessage(error, 'Unable to update your preferences.');
      }
    });
  }

  updateCurrentUserSkipProvidedByPage(value: boolean): void {
    if (!this.authToken) {
      return;
    }

    this.currentUserSkipProvidedByPage = value;
    this.applyProvidedByPreferenceToNewQrForms();
    this.http.patch<CurrentUserPreferencesResponse>(
      this.apiV1Url('current_user_preferences'),
      { user: { skip_provided_by_page: value } },
      { headers: this.authHeaders() }
    ).subscribe({
      next: (response) => {
        this.currentUserDoNotTrack = response.data.do_not_track;
        this.currentUserSkipProvidedByPage = Boolean(response.data.skip_provided_by_page);
        this.applyProvidedByPreferenceToNewQrForms();
      },
      error: (error) => {
        if (this.handleAuthError(error)) {
          return;
        }

        this.currentUserSkipProvidedByPage = !value;
        this.applyProvidedByPreferenceToNewQrForms();
        this.authMessage = this.apiErrorMessage(error, 'Unable to update your preferences.');
      }
    });
  }

  loadApiTokens(): void {
    if (!this.authToken || this.isLoadingApiTokens) {
      return;
    }

    this.isLoadingApiTokens = true;
    this.http.get<ApiTokensResponse>(
      this.apiV1Url('api_tokens'),
      { headers: this.authHeaders() }
    ).subscribe({
      next: (response) => {
        this.apiTokens = response.data;
        this.syncApiTokenDateLabels();
        this.isLoadingApiTokens = false;
      },
      error: (error) => {
        if (this.handleAuthError(error)) {
          this.isLoadingApiTokens = false;
          return;
        }

        this.apiTokenMessage = this.apiErrorMessage(error, 'Unable to load API tokens.');
        this.isLoadingApiTokens = false;
      }
    });
  }

  createApiToken(): void {
    if (!this.authToken || this.isCreatingApiToken) {
      return;
    }

    this.isCreatingApiToken = true;
    this.apiTokenMessage = '';
    this.http.post<ApiTokenResponse>(
      this.apiV1Url('api_tokens'),
      {
        api_token: {
          expires_at: this.apiTokenForm.controls.neverExpires.value ? null : this.apiTokenExpiresAtIso(),
          never_expires: this.apiTokenForm.controls.neverExpires.value
        }
      },
      { headers: this.authHeaders() }
    ).subscribe({
      next: (response) => {
        this.apiTokenValue = response.data.token || '';
        this.apiTokenValueId = response.data.id;
        this.apiTokens = [
          response.data,
          ...this.apiTokens.filter((apiToken) => apiToken.id !== response.data.id)
        ];
        this.syncApiTokenDateLabels();
        this.apiTokenMessage = 'API token created. Copy it now; it will not be shown again.';
        this.isCreatingApiToken = false;
      },
      error: (error) => {
        if (this.handleAuthError(error)) {
          this.isCreatingApiToken = false;
          return;
        }

        this.apiTokenMessage = this.apiErrorMessage(error, 'Unable to create API token.');
        this.isCreatingApiToken = false;
      }
    });
  }

  deleteApiToken(apiToken: ApiTokenRecord): void {
    if (!this.authToken || this.deletingApiTokenId !== null) {
      return;
    }

    this.deletingApiTokenId = apiToken.id;
    this.http.delete(this.apiV1Url(`api_tokens/${apiToken.id}`), {
      headers: this.authHeaders()
    }).subscribe({
      next: () => {
        this.apiTokens = this.apiTokens.filter((token) => token.id !== apiToken.id);
        this.syncApiTokenDateLabels();
        if (this.apiTokenValueId === apiToken.id) {
          this.apiTokenValue = '';
          this.apiTokenValueId = null;
        }
        this.apiTokenMessage = 'API token revoked.';
        this.deletingApiTokenId = null;
      },
      error: (error) => {
        if (this.handleAuthError(error)) {
          this.deletingApiTokenId = null;
          return;
        }

        this.apiTokenMessage = this.apiErrorMessage(error, 'Unable to revoke API token.');
        this.deletingApiTokenId = null;
      }
    });
  }

  async copyApiToken(): Promise<void> {
    if (!this.apiTokenValue) {
      return;
    }

    try {
      await navigator.clipboard.writeText(this.apiTokenValue);
      this.apiTokenMessage = 'API token copied.';
    } catch {
      this.apiTokenMessage = 'Unable to copy API token.';
    }
  }

  async copyMarketingMcpServerUrl(): Promise<void> {
    try {
      await navigator.clipboard.writeText(this.marketingMcpServerUrl);
      this.apiTokenMessage = 'MCP server URL copied.';
    } catch {
      this.apiTokenMessage = 'Unable to copy MCP server URL.';
    }
  }

  trackCompanyContext(_index: number, company: CompanyContextCompany): number {
    return company.id;
  }

  trackCompanyInvitation(_index: number, invitation: AccountContextResponse['pending_invitations'][number]): string {
    return invitation.token;
  }

  trackApiToken(_index: number, apiToken: ApiTokenRecord): number {
    return apiToken.id;
  }

  private syncApiTokenDateLabels(): void {
    this.apiTokenDateLabels = this.apiTokens.reduce<Record<number, string>>((labels, apiToken) => {
      labels[apiToken.id] = apiToken.never_expires ? 'Never expires' : `Expires ${this.formatApiTokenDate(apiToken.expires_at)}`;
      return labels;
    }, {});
  }

  private formatApiTokenDate(value: string | null): string {
    if (!value) {
      return 'Never';
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return '';
    }

    return new Intl.DateTimeFormat(undefined, {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    }).format(date);
  }

  private apiTokenExpiresAtIso(): string | null {
    const value = this.apiTokenForm.controls.expiresAt.value;
    if (!value) {
      return null;
    }

    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date.toISOString();
  }

  private defaultApiTokenExpiresAt(): string {
    const date = new Date(Date.now() + 60 * 60 * 1000);
    const offsetMs = date.getTimezoneOffset() * 60 * 1000;
    return new Date(date.getTime() - offsetMs).toISOString().slice(0, 16);
  }

  async saveBusinessCard(): Promise<void> {
    this.syncBusinessCardFormFromContent();

    if (this.businessCardForm.invalid) {
      this.businessCardForm.markAllAsTouched();
      this.businessCardMessage = 'Add at least one text section before saving this card.';
      return;
    }

    this.isSavingBusinessCard = true;
    this.businessCardMessage = '';

    if (!this.authToken) {
      this.generatedBusinessCard = this.previewBusinessCardRecord();
      this.businessCardMessage = 'Preview ready. Sign in with Google to save business cards.';
      this.isSavingBusinessCard = false;
      return;
    }

    const currentCardId = this.generatedBusinessCard?.id || 0;
    const request = currentCardId > 0
      ? this.http.patch<BusinessCardResponse>(this.apiV1Url(`business_cards/${currentCardId}`), {
          business_card: this.businessCardPayload()
        }, { headers: this.authHeaders() })
      : this.http.post<BusinessCardResponse>(this.apiV1Url('business_cards'), {
      business_card: this.businessCardPayload()
        }, { headers: this.authHeaders() });

    request.subscribe({
      next: async (response) => {
        this.generatedBusinessCard = response.data;
        this.businessCardLayout = this.normalizedBusinessCardLayout(response.data.card_layout);
        this.mergeCategoryNames(response.data.category_names);
        this.upsertBusinessCard(response.data);
        this.syncBusinessCardLimitNotice(response.meta);
        await this.syncBusinessCardQrPreview(response.data);
        this.businessCardMessage = 'Business card saved.';
        this.isSavingBusinessCard = false;
      },
      error: (error) => {
        if (this.handleAuthError(error)) {
          this.isSavingBusinessCard = false;
          return;
        }

        this.syncBusinessCardLimitNotice(error.error?.meta);
        this.businessCardMessage = this.apiErrorMessage(error, 'Unable to save business card.');
        this.isSavingBusinessCard = false;
      }
    });
  }

  async saveNewsletter(): Promise<void> {
    await this.persistNewsletter(false);
  }

  queueNewsletterAutosave(): void {
    this.newsletterAutosave$.next();
  }

  private async autosaveNewsletter(): Promise<void> {
    if (this.selectedTool !== 'marketingEmail' || this.isSavingNewsletter) {
      this.newsletterAutosavePending = this.isSavingNewsletter;
      return;
    }

    await this.persistNewsletter(true);
  }

  private async persistNewsletter(isAutosave: boolean): Promise<void> {
    if (this.newsletterTemplateLimitReached()) {
      this.newsletterMessage = 'You have 5 saved newsletter templates. Delete a template or edit an existing template before creating another.';
      return;
    }

    if (this.newsletterForm.invalid) {
      if (!isAutosave) {
        this.newsletterForm.markAllAsTouched();
        this.newsletterMessage = 'Add a title before saving this news letter.';
      }
      return;
    }

    if (!this.authToken) {
      if (!isAutosave) {
        this.newsletterMessage = 'Sign in with Google to save news letters.';
      }
      return;
    }

    this.isSavingNewsletter = true;
    if (!isAutosave) {
      this.newsletterMessage = '';
    }

    const currentNewsletterId = this.generatedNewsletter?.id || 0;
    const payload = this.newsletterPayload();
    const request = currentNewsletterId > 0
      ? this.http.patch<NewsletterResponse>(this.apiV1Url(`newsletters/${currentNewsletterId}`), {
          newsletter: payload
        }, { headers: this.authHeaders() })
      : this.http.post<NewsletterResponse>(this.apiV1Url('newsletters'), {
          newsletter: payload
        }, { headers: this.authHeaders() });

    try {
      const response = await firstValueFrom(request);
      this.generatedNewsletter = response.data;
      this.newsletterEditor?.acceptCurrentNewsletter(response.data.id);
      this.newsletterForm.controls.categoryNames.setValue(response.data.category_names.join(', '));
      this.mergeCategoryNames(response.data.category_names);
      this.upsertNewsletter(response.data);
      this.newsletterMessage = isAutosave ? 'News letter autosaved.' : 'News letter saved.';
    } catch (error) {
      if (!this.handleAuthError(error)) {
        this.newsletterMessage = this.apiErrorMessage(error, 'Unable to save news letter.');
      }
    } finally {
      this.isSavingNewsletter = false;
      if (this.newsletterAutosavePending) {
        this.newsletterAutosavePending = false;
        this.queueNewsletterAutosave();
      }
    }
  }

  selectNewsletter(record: NewsletterRecord): void {
    this.generatedNewsletter = record;
    this.newsletterForm.patchValue({
      title: record.title,
      categoryNames: record.category_names.join(', ')
    });
    this.newsletterMessage = '';
  }

  newNewsletter(): void {
    this.generatedNewsletter = null;
    this.newsletterForm.reset({
      title: '',
      categoryNames: ''
    });
    this.newsletterEditor?.clear();
    this.newsletterMessage = '';
  }

  newsletterTemplateLimitReached(): boolean {
    return !this.generatedNewsletter && this.newsletters.length >= NEWSLETTER_TEMPLATE_LIMIT;
  }

  usageLimitNoticeTitle(): string {
    if (this.selectedTool === 'businessCard' && this.businessCardUsageLimitReached()) {
      return 'Business card limit reached';
    }

    if (this.selectedTool === 'nfc') {
      return this.nfcTagUsageLimitReached() ? 'NFC tag limit reached' : '';
    }

    return 'QR code limit reached';
  }

  usageLimitNoticeMessage(): string {
    if (this.selectedTool === 'businessCard' && this.businessCardUsageLimitReached()) {
      return this.businessCardLimitMessage
        || 'You have 5 saved business cards. Delete a card or edit an existing card before creating another.';
    }

    if (this.selectedTool === 'nfc') {
      return this.nfcTagUsageLimitReached()
        ? this.nfcTagLimitMessage
          || 'You have 5 saved NFC tags. Subscribe for $10/month through Stripe to unlock unlimited use.'
        : '';
    }

    return this.limitNoticeMessage;
  }

  async deleteNewsletter(record: NewsletterRecord): Promise<void> {
    if (!this.authToken || !(await this.confirmDeleteNewsletter(record))) {
      return;
    }

    this.deletingNewsletterId = record.id;
    this.newsletterMessage = '';

    this.http.delete(this.apiV1Url(`newsletters/${record.id}`), {
      headers: this.authHeaders()
    }).subscribe({
      next: () => {
        this.newsletters = this.newsletters.filter((newsletter) => newsletter.id !== record.id);
        if (this.generatedNewsletter?.id === record.id) {
          this.newNewsletter();
        }
        this.deletingNewsletterId = null;
      },
      error: (error) => {
        if (this.handleAuthError(error)) {
          this.deletingNewsletterId = null;
          return;
        }

        this.newsletterMessage = this.apiErrorMessage(error, 'Unable to delete news letter.');
        this.deletingNewsletterId = null;
      }
    });
  }

  async selectBusinessCard(record: BusinessCardRecord): Promise<void> {
    this.generatedBusinessCard = record;
    this.businessCardLayout = this.normalizedBusinessCardLayout(record.card_layout);
    this.businessCardContent = this.normalizedBusinessCardContent(record.card_content, record);
    this.businessCardQrCodes = this.businessCardQrCodesFor(record);
    this.businessCardForm.reset({
      name: record.name,
      jobTitle: record.job_title || '',
      company: record.company || '',
      email: record.email || '',
      phone: record.phone || '',
      website: record.website || '',
      address: record.address || '',
      tagline: record.tagline || '',
      categoryNames: record.category_names.join(', ')
    }, { emitEvent: false });
    await this.syncBusinessCardQrPreview(record);
    this.scheduleBusinessCardTrixSync();
  }

  newBusinessCard(): void {
    this.generatedBusinessCard = null;
    this.businessCardLayout = this.defaultBusinessCardLayout();
    this.businessCardContent = this.defaultBusinessCardContent();
    this.businessCardQrCodes = [];
    this.businessCardQrImages = {};
    this.businessCardQrImageUrl = '';
    this.selectedBusinessCardSide = 'front';
    this.selectedExistingBusinessCardQrCodeId = null;
    this.businessCardForm.reset({
      name: '',
      jobTitle: '',
      company: '',
      email: '',
      phone: '',
      website: '',
      address: '',
      tagline: '',
      categoryNames: ''
    }, { emitEvent: false });
    this.businessCardQrForm.reset({
      title: '',
      targetUrl: '',
      userAgentRoutes: '',
      qrLabelText: '',
      qrIconSize: '28',
      qrLabelSize: '100',
      qrStyle: 'classic',
      categoryNames: '',
      trackAnalytics: Boolean(this.authToken),
      providedByPage: this.defaultProvidedByPage()
    }, { emitEvent: false });
    this.syncBusinessCardTrackedAnalyticsControl();
    this.businessCardMessage = '';
    this.businessCardNfcMessage = '';
    this.scheduleBusinessCardTrixSync();
  }

  selectBusinessCardSide(side: BusinessCardSide): void {
    this.selectedBusinessCardSide = side;
    this.scheduleBusinessCardTrixSync();
  }

  focusBusinessCardTextSection(sectionId: string): void {
    void this.ensureBusinessCardEditorLoaded().then(() => {
      this.syncVisibleBusinessCardTrixEditors(true);
      window.setTimeout(() => {
        const inputId = this.businessCardTrixInputId(sectionId);
        const editor = document.querySelector<BusinessCardTrixEditorElement>(`trix-editor[input="${inputId}"]`);
        editor?.scrollIntoView({ block: 'center', behavior: 'smooth' });
        editor?.focus();
      });
    });
  }

  businessCardElementStyle(elementId: BusinessCardElementId): Record<string, string> {
    return this.businessCardElementStyleForSide(this.selectedBusinessCardSide, elementId);
  }

  businessCardPrintElementStyle(side: BusinessCardSide, elementId: BusinessCardElementId): Record<string, string> {
    return this.businessCardElementStyleForSide(side, elementId);
  }

  private businessCardElementStyleForSide(side: BusinessCardSide, elementId: BusinessCardElementId): Record<string, string> {
    const position = this.positionForElementOnSide(side, elementId);
    const style: Record<string, string> = {
      left: `${position.x}%`,
      top: `${position.y}%`,
      maxWidth: `calc(100% - ${position.x}% - var(--business-card-edge-offset, 4px))`,
      maxHeight: `calc(100% - ${position.y}% - var(--business-card-edge-offset, 4px))`
    };

    if (elementId.startsWith('qr-')) {
      const size = this.businessCardQrCodeSizeForElement(side, elementId);
      style['width'] = `${size}%`;
      style['height'] = 'auto';
      delete style['maxWidth'];
      delete style['maxHeight'];
    }

    return style;
  }

  businessCardTextSectionsForSelectedSide(): BusinessCardTextSection[] {
    return this.businessCardTextSectionsForSide(this.selectedBusinessCardSide);
  }

  businessCardTextSectionsForSide(side: BusinessCardSide): BusinessCardTextSection[] {
    return this.businessCardContent[side].filter((section): section is BusinessCardTextSection => (
      section.type === 'text'
    ));
  }

  businessCardLogoSectionsForSelectedSide(): BusinessCardLogoSection[] {
    return this.businessCardLogoSectionsForSide(this.selectedBusinessCardSide);
  }

  businessCardLogoSectionsForSide(side: BusinessCardSide): BusinessCardLogoSection[] {
    return this.businessCardContent[side].filter((section): section is BusinessCardLogoSection => (
      section.type === 'logo'
    ));
  }

  addBusinessCardTextSection(): void {
    const side = this.selectedBusinessCardSide;
    const section: BusinessCardTextSection = {
      id: this.newBusinessCardTextSectionId(),
      type: 'text',
      text: '',
      html: '',
      size: 'normal'
    };

    this.businessCardContent = {
      ...this.businessCardContent,
      [side]: [...this.businessCardContent[side], section]
    };

    this.ensureTextSectionLayout(section.id, side);
    this.syncBusinessCardFormFromContent();
    this.scheduleBusinessCardTrixSync();
  }

  async addBusinessCardLogoSections(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files || []);
    if (files.length === 0) {
      return;
    }

    const side = this.selectedBusinessCardSide;
    const sections: BusinessCardLogoSection[] = [];

    try {
      for (const file of files) {
        sections.push({
          id: this.newBusinessCardLogoSectionId(),
          type: 'logo',
          dataUrl: await this.compressLogoDataUrl(file)
        });
      }
    } catch {
      this.businessCardMessage = 'Unable to process one of those logos. Try smaller image files.';
      input.value = '';
      return;
    }

    this.businessCardContent = {
      ...this.businessCardContent,
      [side]: [...this.businessCardContent[side], ...sections]
    };

    sections.forEach((section) => this.ensureLogoSectionLayout(section.id, side));
    input.value = '';
  }

  updateBusinessCardTextSection(sectionId: string, text: string | null | undefined): void {
    this.updateBusinessCardTextSectionContent(sectionId, text || '', this.businessCardTextToHtml(text || ''));
  }

  updateBusinessCardTextSectionHtml(sectionId: string, html: string | null | undefined): void {
    const sanitizedHtml = this.sanitizeBusinessCardRichText(html || '');
    this.updateBusinessCardTextSectionContent(sectionId, this.businessCardPlainTextFromHtml(sanitizedHtml), sanitizedHtml);
  }

  businessCardTrixInputId(sectionId: string): string {
    return `business-card-trix-${sectionId.replace(/[^a-zA-Z0-9_-]/g, '-')}`;
  }

  businessCardTextSectionHtml(section: BusinessCardTextSection): string {
    return this.sanitizeBusinessCardRichText(section.html || this.businessCardTextToHtml(section.text));
  }

  syncBusinessCardTextEditor(sectionId: string, force = true, editorElement?: BusinessCardTrixEditorElement | null): void {
    const section = this.businessCardTextSectionsForSelectedSide().find((candidate) => candidate.id === sectionId);
    if (!section) {
      return;
    }

    const inputId = this.businessCardTrixInputId(section.id);
    const html = this.businessCardTextSectionHtml(section);
    const input = document.getElementById(inputId) as HTMLInputElement | null;
    const editor = editorElement || document.querySelector<BusinessCardTrixEditorElement>(`trix-editor[input="${inputId}"]`);

    if (input) {
      input.value = html;
    }

    if (editor && (force || editor.value !== html)) {
      editor.editor?.loadHTML(html);
    }
  }

  businessCardTextToHtml(text: string): string {
    const escapedText = this.escapeHtml(text);
    return escapedText.split(/\r?\n/).map((line) => `<div>${line || '<br>'}</div>`).join('');
  }

  updateBusinessCardTextSectionSize(sectionId: string, size: BusinessCardTextSize | string | null | undefined): void {
    const side = this.selectedBusinessCardSide;
    const normalizedSize = this.normalizedBusinessCardTextSize(size);
    this.businessCardContent = {
      ...this.businessCardContent,
      [side]: this.businessCardContent[side].map((section) => (
        section.id === sectionId && section.type === 'text' ? { ...section, size: normalizedSize } : section
      ))
    };
    this.syncBusinessCardFormFromContent();
  }

  updateBusinessCardTextSectionColor(sectionId: string, color: string | null | undefined): void {
    const side = this.selectedBusinessCardSide;
    const normalizedColor = this.normalizeHexColor(color, this.businessCardDefaultTextColor());
    this.businessCardContent = {
      ...this.businessCardContent,
      [side]: this.businessCardContent[side].map((section) => (
        section.id === sectionId && section.type === 'text' ? { ...section, color: normalizedColor } : section
      ))
    };
  }

  businessCardTextSectionFontSize(section: BusinessCardTextSection): string {
    return this.businessCardTextSizeFontSize(section.size);
  }

  businessCardTextSectionColor(section: BusinessCardTextSection): string {
    return this.normalizeHexColor(section.color, this.businessCardDefaultTextColor());
  }

  private updateBusinessCardTextSectionContent(sectionId: string, text: string, html: string): void {
    const side = this.selectedBusinessCardSide;
    this.businessCardContent = {
      ...this.businessCardContent,
      [side]: this.businessCardContent[side].map((section) => (
        section.id === sectionId && section.type === 'text' ? { ...section, text, html } : section
      ))
    };
    this.syncBusinessCardFormFromContent();
  }

  removeBusinessCardTextSection(sectionId: string): void {
    const side = this.selectedBusinessCardSide;
    this.businessCardContent = {
      ...this.businessCardContent,
      [side]: this.businessCardContent[side].filter((section) => section.id !== sectionId)
    };

    const remainingLayout = { ...this.businessCardLayout[side] };
    delete remainingLayout[sectionId];
    this.businessCardLayout = {
      ...this.businessCardLayout,
      [side]: remainingLayout
    };
    this.syncBusinessCardFormFromContent();
  }

  removeBusinessCardLogoSection(sectionId: string): void {
    const side = this.selectedBusinessCardSide;
    this.businessCardContent = {
      ...this.businessCardContent,
      [side]: this.businessCardContent[side].filter((section) => section.id !== sectionId)
    };

    const remainingLayout = { ...this.businessCardLayout[side] };
    delete remainingLayout[sectionId];
    this.businessCardLayout = {
      ...this.businessCardLayout,
      [side]: remainingLayout
    };
  }

  trackBusinessCardTextSection(_index: number, section: BusinessCardTextSection): string {
    return section.id;
  }

  trackBusinessCardLogoSection(_index: number, section: BusinessCardLogoSection): string {
    return section.id;
  }

  businessCardQrCodeSize(qrCode: QrCodeRecord): number {
    return this.businessCardQrCodeSizeForElement(this.selectedBusinessCardSide, this.qrElementId(qrCode));
  }

  updateBusinessCardQrCodeSize(qrCode: QrCodeRecord, value: number | string | null | undefined): void {
    const elementId = this.qrElementId(qrCode);
    const side = this.selectedBusinessCardSide;
    const currentPosition = this.businessCardLayout[side][elementId] || { x: 68, y: 18 };

    this.businessCardLayout = {
      ...this.businessCardLayout,
      [side]: {
        ...this.businessCardLayout[side],
        [elementId]: {
          ...currentPosition,
          size: this.normalizedBusinessCardQrCodeSize(value)
        }
      }
    };
  }

  businessCardAccentColor(): string {
    return this.businessCardContent.background.front.color || '#164c63';
  }

  businessCardBackgroundForSelectedSide(): BusinessCardBackground {
    return this.businessCardContent.background[this.selectedBusinessCardSide];
  }

  businessCardBackgroundStyle(side = this.selectedBusinessCardSide): Record<string, string> {
    const backgroundColor = this.businessCardContent.background[side].color;
    const foregroundColor = this.readableTextColorForHex(backgroundColor);

    return {
      '--card-accent': backgroundColor,
      '--card-foreground': foregroundColor,
      '--card-muted': foregroundColor,
      ...this.businessCardPrint.businessCardBackgroundCss(this.businessCardContent.background[side])
    };
  }

  businessCardGridStyle(): Record<string, string> {
    return {
      '--business-card-grid-size': `${this.businessCardGridSize}%`
    };
  }

  updateBusinessCardGridSize(value: number | string | null | undefined): void {
    this.businessCardGridSize = this.normalizedBusinessCardGridSize(value);
  }

  snapBusinessCardTextSectionsToGrid(): void {
    const side = this.selectedBusinessCardSide;
    const textSectionIds = new Set(this.businessCardTextSectionsForSide(side).map((section) => section.id));
    const sideLayout = this.businessCardLayout[side];
    const snappedLayout = Object.fromEntries(
      Object.entries(sideLayout).map(([elementId, position]) => [
        elementId,
        textSectionIds.has(elementId)
          ? {
              ...position,
              x: this.snapBusinessCardPercent(position.x),
              y: this.snapBusinessCardPercent(position.y)
            }
          : position
      ])
    );

    this.businessCardLayout = {
      ...this.businessCardLayout,
      [side]: snappedLayout
    };
  }

  updateBusinessCardBackgroundColor(value: string | null | undefined): void {
    this.updateBusinessCardBackground({ color: this.normalizeHexColor(value, this.businessCardBackgroundForSelectedSide().color) });
  }

  updateBusinessCardBackgroundFade(value: number | string | null | undefined): void {
    this.updateBusinessCardBackground({ fade: this.clampBackgroundFade(Number(value)) });
  }

  updateBusinessCardBackgroundStyle(value: string | null | undefined): void {
    this.updateBusinessCardBackground({ style: this.normalizeBusinessCardBackgroundStyle(value) });
  }

  async onBusinessCardBackgroundImageSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];

    if (!file) {
      return;
    }

    try {
      this.updateBusinessCardBackground({ imageDataUrl: await this.compressLogoDataUrl(file) });
    } catch {
      this.businessCardMessage = 'Unable to process that background picture. Try a smaller image file.';
    } finally {
      input.value = '';
    }
  }

  removeBusinessCardBackgroundImage(): void {
    this.updateBusinessCardBackground({ imageDataUrl: '' });
  }

  matchBusinessCardBackgroundFromOtherSide(): void {
    const targetSide = this.selectedBusinessCardSide;
    const sourceSide: BusinessCardSide = targetSide === 'front' ? 'back' : 'front';
    const sourceBackground = this.businessCardContent.background[sourceSide];

    this.businessCardContent = {
      ...this.businessCardContent,
      background: {
        ...this.businessCardContent.background,
        [targetSide]: { ...sourceBackground }
      }
    };
  }

  private updateBusinessCardBackground(changes: Partial<BusinessCardBackground>): void {
    const side = this.selectedBusinessCardSide;
    this.businessCardContent = {
      ...this.businessCardContent,
      background: {
        ...this.businessCardContent.background,
        [side]: {
          ...this.businessCardContent.background[side],
          ...changes
        }
      }
    };
  }

  startBusinessCardDrag(event: PointerEvent, elementId: BusinessCardElementId): void {
    const card = (event.currentTarget as HTMLElement).closest('.business-card') as HTMLElement | null;
    if (!card) {
      return;
    }

    const bounds = card.getBoundingClientRect();
    const origin = this.positionForElement(elementId);
    this.activeBusinessCardDrag = {
      elementId,
      side: this.selectedBusinessCardSide,
      pointerId: event.pointerId,
      startClientX: event.clientX,
      startClientY: event.clientY,
      originX: origin.x,
      originY: origin.y,
      cardWidth: bounds.width,
      cardHeight: bounds.height
    };

    (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);
    event.preventDefault();
  }

  startBusinessCardDragFromPreview(dragEvent: BusinessCardDragEvent): void {
    this.startBusinessCardDrag(dragEvent.event, dragEvent.elementId);
  }

  moveBusinessCardElement(event: PointerEvent): void {
    const drag = this.activeBusinessCardDrag;
    if (!drag || event.pointerId !== drag.pointerId) {
      return;
    }

    const deltaX = ((event.clientX - drag.startClientX) / drag.cardWidth) * 100;
    const deltaY = ((event.clientY - drag.startClientY) / drag.cardHeight) * 100;
    const currentPosition = this.businessCardLayout[drag.side][drag.elementId] || {};

    this.businessCardLayout = {
      ...this.businessCardLayout,
      [drag.side]: {
        ...this.businessCardLayout[drag.side],
        [drag.elementId]: {
          ...currentPosition,
          x: this.positionBusinessCardPercent(drag.originX + deltaX),
          y: this.positionBusinessCardPercent(drag.originY + deltaY)
        }
      }
    };
  }

  stopBusinessCardDrag(event: PointerEvent): void {
    if (this.activeBusinessCardDrag?.pointerId === event.pointerId) {
      this.activeBusinessCardDrag = null;
    }
  }

  async addQrCodeToBusinessCard(): Promise<void> {
    this.syncBusinessCardFormFromContent();

    const qrValue = this.businessCardQrForm.getRawValue();
    const targetUrl = qrValue.targetUrl.trim() || this.businessCardForm.controls.website.value.trim();

    if (!targetUrl) {
      this.businessCardMessage = 'Add a website before creating a QR code for this card.';
      return;
    }

    this.isSavingBusinessCard = true;
    this.businessCardMessage = '';

    let validatedUrl: string;

    try {
      validatedUrl = await this.validateQrDestinationUrl(targetUrl);
    } catch (error) {
      this.businessCardMessage = this.validationErrorMessage(error);
      this.isSavingBusinessCard = false;
      return;
    }

    if (!this.authToken) {
      const previewRecord = this.previewQrCodeRecord(validatedUrl, qrValue.qrStyle);
      this.businessCardQrCodes = [...this.businessCardQrCodes, previewRecord];
      this.businessCardQrImages[previewRecord.id] = await this.qrRender.qrImageDataUrl(
        validatedUrl,
        this.selectedQrStyle(qrValue.qrStyle),
        {
          qrLabelText: previewRecord.qr_label_text || '',
          qrIconSize: previewRecord.qr_icon_size,
          qrLabelSize: previewRecord.qr_label_size,
          logoImage: this.logoImage
        }
      );
      this.ensureQrLayout(previewRecord.id);
      this.generatedBusinessCard = this.previewBusinessCardRecord();
      this.businessCardMessage = 'QR added to the preview. Sign in to save a tracked QR with the card.';
      this.isSavingBusinessCard = false;
      return;
    }

    const title = qrValue.title || `${this.businessCardForm.controls.name.value || 'Business card'} QR`;
    this.http.post<QrCodeResponse>(this.apiV1Url('qr_codes'), {
      qr_code: {
        title,
        target_url: validatedUrl,
        track_analytics: qrValue.trackAnalytics,
        provided_by_page: qrValue.providedByPage,
        qr_style: qrValue.qrStyle,
        qr_label_text: qrValue.qrLabelText || null,
        qr_icon_size: this.normalizedQrIconSize(qrValue.qrIconSize),
        qr_label_size: this.normalizedQrLabelSize(qrValue.qrLabelSize),
        logo_data_url: this.uploadedLogoDataUrl || null,
        category_names: this.categoryNamesFromInput(qrValue.categoryNames)
      }
    }, { headers: this.authHeaders() }).subscribe({
      next: async (response) => {
        this.generatedRecord = response.data;
        this.upsertQrCode(response.data);
        this.mergeCategoryNames(response.data.category_names);
        this.syncLimitNotice(response.meta);
        this.activeQrLabelText = response.data.qr_label_text || '';
        this.activeQrIconSize = response.data.qr_icon_size;
        this.activeQrLabelSize = response.data.qr_label_size;
        this.activeQrStickerStyle = response.data.qr_style;
        this.businessCardQrCodes = [
          ...this.businessCardQrCodes.filter((qrCode) => qrCode.id !== response.data.id),
          response.data
        ];
        this.businessCardQrImages[response.data.id] = await this.qrImageDataUrlForRecord(response.data);
        this.ensureQrLayout(response.data.id);
        await this.attachQrCodeToBusinessCard(response.data);
        this.businessCardMessage = 'QR code added to the business card.';
        this.isSavingBusinessCard = false;
      },
      error: (error) => {
        if (this.handleAuthError(error)) {
          this.isSavingBusinessCard = false;
          return;
        }

        this.syncLimitNotice(error.error?.meta);
        this.businessCardMessage = this.apiErrorMessage(error, 'Unable to create QR code for this card.');
        this.isSavingBusinessCard = false;
      }
    });
  }

  async onBusinessCardLogoSelected(event: Event): Promise<void> {
    await this.onLogoSelected(event);
  }

  async addExistingQrCodeToBusinessCard(record: QrCodeRecord): Promise<void> {
    this.businessCardQrCodes = [
      ...this.businessCardQrCodes.filter((qrCode) => qrCode.id !== record.id),
      record
    ];
    this.businessCardQrImages[record.id] = await this.qrImageDataUrlForRecord(record);
    this.ensureQrLayout(record.id);
    this.generatedBusinessCard = {
      ...this.previewBusinessCardRecord(),
      qr_code_id: this.businessCardQrCodes[0]?.id || null,
      qr_code: this.businessCardQrCodes[0] || null,
      qr_code_ids: this.businessCardQrCodes.filter((qrCode) => qrCode.id > 0).map((qrCode) => qrCode.id),
      qr_codes: this.businessCardQrCodes.filter((qrCode) => qrCode.id > 0)
    };
  }

  async addExistingQrCodeToBusinessCardFromPicker(record: QrCodeRecord): Promise<void> {
    this.selectedExistingBusinessCardQrCodeId = null;
    await this.addExistingQrCodeToBusinessCard(record);
  }

  selectExistingNfcQrCode(record: QrCodeRecord): void {
    this.applyNfcQrCode(record);
  }

  isNfcQrCodeSearchVisible(): boolean {
    return this.showNfcQrCodeSearch || !this.selectedNfcQrCode;
  }

  openNfcQrCodeSearch(): void {
    this.showNfcQrCodeSearch = true;
    this.loadDefaultQrCodePickerResults();
  }

  clearNfcQrCodeSelection(): void {
    this.selectedNfcQrCode = null;
    this.selectedExistingNfcQrCodeId = null;
    this.showNfcQrCodeSearch = true;
    this.businessCardForm.controls.website.setValue('', { emitEvent: false });
    this.businessCardNfcMessage = '';
    this.nfcTagMessage = '';
  }

  nfcQrCodeDisplayUrl(record: QrCodeRecord): string {
    return this.nfcQrCodeUrl(record);
  }

  async writeBusinessCardNfcTag(): Promise<void> {
    this.syncBusinessCardFormFromContent(false);

    const payload = this.currentNfcTagPayload();
    if (!payload) {
      return;
    }

    if (!this.canWriteBusinessCardNfc) {
      this.businessCardNfcMessage = 'NFC writing is unavailable on this device.';
      return;
    }

    this.writeBusinessCardNfcController = new AbortController();
    this.isWritingBusinessCardNfc = true;
    this.businessCardNfcMessage = 'Hold a writable NFC tag near this device.';
    const writeController = this.writeBusinessCardNfcController;

    try {
      await this.nfcWriter.writePayload(payload, {
        signal: writeController.signal,
        lockAfterWrite: this.lockBusinessCardNfcAfterWrite,
        onWrite: () => {
          this.businessCardNfcMessage = this.lockBusinessCardNfcAfterWrite
            ? 'NFC tag written and locked.'
            : 'NFC tag written.';
        }
      });
      this.businessCardNfcMessage = this.lockBusinessCardNfcAfterWrite
        ? 'NFC tag written and locked.'
        : 'NFC tag written.';
    } catch (error) {
      this.businessCardNfcMessage = writeController.signal.aborted
        ? 'NFC write canceled.'
        : this.nfcErrorMessage(error);
    } finally {
      this.isWritingBusinessCardNfc = false;
      if (this.writeBusinessCardNfcController === writeController) {
        this.writeBusinessCardNfcController = null;
      }
    }
  }

  stopWriteBusinessCardNfcTag(): void {
    if (!this.isWritingBusinessCardNfc) {
      return;
    }

    this.writeBusinessCardNfcController?.abort();
    this.businessCardNfcMessage = 'NFC write canceled.';
  }

  toggleBusinessCardNfcLockAfterWrite(event?: Event): void {
    const eventPath = event?.composedPath?.() || [];
    if (eventPath.some((target) => target instanceof HTMLElement && target.tagName === 'ION-TOGGLE')) {
      return;
    }

    if (!this.canLockBusinessCardNfc || this.isWritingBusinessCardNfc || this.isMassWritingBusinessCardNfc) {
      return;
    }

    this.lockBusinessCardNfcAfterWrite = !this.lockBusinessCardNfcAfterWrite;
  }

  async massWriteBusinessCardNfcTags(): Promise<void> {
    this.syncBusinessCardFormFromContent(false);

    const payload = this.currentNfcTagPayload();
    if (!payload) {
      return;
    }

    if (!this.canWriteBusinessCardNfc) {
      this.businessCardNfcMessage = 'NFC writing is unavailable on this device.';
      return;
    }

    this.massWriteBusinessCardNfcController = new AbortController();
    this.isMassWritingBusinessCardNfc = true;
    this.massWrittenBusinessCardNfcCount = 0;
    this.businessCardNfcMessage = 'Mass write ready. Hold each writable NFC tag near this device.';

    try {
      await this.nfcWriter.massWritePayload(payload, {
        signal: this.massWriteBusinessCardNfcController.signal,
        lockAfterWrite: this.lockBusinessCardNfcAfterWrite,
        onWrite: (count) => {
          this.massWrittenBusinessCardNfcCount = count;
          const action = this.lockBusinessCardNfcAfterWrite ? 'written and locked' : 'written';
          this.businessCardNfcMessage = `${count} NFC ${count === 1 ? 'tag' : 'tags'} ${action}. Hold the next tag near this device.`;
        }
      });
    } catch (error) {
      if (!this.massWriteBusinessCardNfcController?.signal.aborted) {
        this.businessCardNfcMessage = this.nfcErrorMessage(error);
      }
    } finally {
      this.isMassWritingBusinessCardNfc = false;
      this.massWriteBusinessCardNfcController = null;
    }
  }

  stopMassWriteBusinessCardNfcTags(): void {
    if (!this.isMassWritingBusinessCardNfc) {
      return;
    }

    this.massWriteBusinessCardNfcController?.abort();
    this.businessCardNfcMessage = this.massWrittenBusinessCardNfcCount > 0
      ? `Mass write stopped after ${this.massWrittenBusinessCardNfcCount} ${this.massWrittenBusinessCardNfcCount === 1 ? 'tag' : 'tags'}.`
      : 'Mass write stopped.';
  }

  async sendBusinessCardNfcTag(): Promise<void> {
    this.syncBusinessCardFormFromContent(false);

    const payload = this.currentNfcTagPayload();
    if (!payload) {
      return;
    }

    if (!this.canBroadcastBusinessCardNfc) {
      this.businessCardNfcMessage = 'NFC tag broadcasting is unavailable on this device.';
      return;
    }

    this.isBroadcastingBusinessCardNfc = true;
    this.businessCardNfcMessage = 'Starting NFC tag broadcast.';

    try {
      await this.nfcWriter.sendTag(payload);
      this.businessCardNfcMessage = 'NFC tag broadcast is ready. Keep this device near the receiver.';
    } catch (error) {
      this.isBroadcastingBusinessCardNfc = false;
      this.businessCardNfcMessage = this.nfcErrorMessage(error, 'Unable to broadcast NFC tag.');
    }
  }

  async stopBusinessCardNfcBroadcast(): Promise<void> {
    if (!this.isBroadcastingBusinessCardNfc || this.isStoppingBusinessCardNfcBroadcast) {
      return;
    }

    this.isStoppingBusinessCardNfcBroadcast = true;

    try {
      await this.nfcWriter.stopBroadcast();
      this.businessCardNfcMessage = 'NFC tag broadcast stopped.';
    } catch (error) {
      this.businessCardNfcMessage = this.nfcErrorMessage(error, 'Unable to stop NFC tag broadcast.');
    } finally {
      this.isBroadcastingBusinessCardNfc = false;
      this.isStoppingBusinessCardNfcBroadcast = false;
    }
  }

  saveNfcTag(): void {
    this.syncBusinessCardFormFromContent(false);

    const payload = this.currentNfcTagPayload();
    if (!payload || this.isSavingNfcTag) {
      return;
    }

    if (!this.authToken) {
      this.nfcTagMessage = 'Sign in with Google to save NFC tags.';
      return;
    }

    this.isSavingNfcTag = true;
    this.nfcTagMessage = '';

    const currentTagId = this.selectedNfcTag?.id || 0;
    const request = currentTagId > 0
      ? this.http.patch<NfcTagResponse>(this.apiV1Url(`nfc_tags/${currentTagId}`), {
          nfc_tag: payload
        }, { headers: this.authHeaders() })
      : this.http.post<NfcTagResponse>(this.apiV1Url('nfc_tags'), {
          nfc_tag: payload
        }, { headers: this.authHeaders() });

    request.subscribe({
      next: (response) => {
        this.selectedNfcTag = response.data;
        this.nfcTagForm.controls.title.setValue(response.data.title, { emitEvent: false });
        this.upsertNfcTag(response.data);
        this.syncNfcTagLimitNotice(response.meta);
        this.nfcTagMessage = 'NFC tag saved.';
        this.isSavingNfcTag = false;
      },
      error: (error) => {
        if (this.handleAuthError(error)) {
          this.isSavingNfcTag = false;
          return;
        }

        this.nfcTagMessage = this.apiErrorMessage(error, 'Unable to save NFC tag.');
        this.isSavingNfcTag = false;
      }
    });
  }

  selectNfcTag(record: NfcTagRecord): void {
    this.selectedNfcTag = record;
    this.selectedNfcQrCode = null;
    this.selectedExistingNfcQrCodeId = null;
    this.businessCardNfcPayloadMode = record.payload_mode;
    this.nfcTagForm.controls.title.setValue(record.title, { emitEvent: false });
    this.businessCardForm.patchValue({
      name: record.contact?.name || '',
      jobTitle: record.contact?.jobTitle || '',
      company: record.contact?.company || '',
      email: record.contact?.email || '',
      phone: record.contact?.phone || '',
      website: record.target_url || record.contact?.website || '',
      address: record.contact?.address || '',
      tagline: record.contact?.tagline || ''
    }, { emitEvent: false });
    this.businessCardNfcMessage = '';
    this.nfcTagMessage = '';
  }

  newNfcTag(): void {
    this.selectedNfcTag = null;
    this.selectedNfcQrCode = null;
    this.selectedExistingNfcQrCodeId = null;
    this.showNfcQrCodeSearch = true;
    this.nfcTagForm.reset({ title: '' }, { emitEvent: false });
    this.businessCardForm.patchValue({
      name: '',
      jobTitle: '',
      company: '',
      email: '',
      phone: '',
      website: '',
      address: '',
      tagline: ''
    }, { emitEvent: false });
    this.businessCardNfcMessage = '';
    this.nfcTagMessage = '';
  }

  deleteNfcTag(record: NfcTagRecord): void {
    if (!this.authToken || this.deletingNfcTagId !== null) {
      return;
    }

    this.deletingNfcTagId = record.id;
    this.nfcTagMessage = '';

    this.http.delete(this.apiV1Url(`nfc_tags/${record.id}`), {
      headers: this.authHeaders()
    }).subscribe({
      next: () => {
        this.nfcTags = this.nfcTags.filter((nfcTag) => nfcTag.id !== record.id);
        if (this.selectedNfcTag?.id === record.id) {
          this.selectedNfcTag = null;
        }
        this.syncNfcTagLimitNoticeFromList();
        this.deletingNfcTagId = null;
      },
      error: (error) => {
        if (this.handleAuthError(error)) {
          this.deletingNfcTagId = null;
          return;
        }

        this.nfcTagMessage = this.apiErrorMessage(error, 'Unable to delete NFC tag.');
        this.deletingNfcTagId = null;
      }
    });
  }

  removeBusinessCardQrCode(record: QrCodeRecord): void {
    const side = this.selectedBusinessCardSide;
    const remainingLayout = { ...this.businessCardLayout[side] };
    delete remainingLayout[this.qrElementId(record)];
    this.businessCardLayout = {
      ...this.businessCardLayout,
      [side]: remainingLayout
    };

    if (!this.qrCodeHasLayoutOnAnySide(record)) {
      this.businessCardQrCodes = this.businessCardQrCodes.filter((qrCode) => qrCode.id !== record.id);
      const remainingImages = { ...this.businessCardQrImages };
      delete remainingImages[record.id];
      this.businessCardQrImages = remainingImages;
    }
  }

  async printBusinessCardSheet(): Promise<void> {
    this.syncBusinessCardFormFromContent();

    if (this.businessCardForm.invalid) {
      this.businessCardForm.markAllAsTouched();
      this.businessCardMessage = 'Add at least one text section before printing this card.';
      return;
    }

    await this.ensureBusinessCardQrImagesForPrint();
    await this.waitForNextRenderTurn();

    const printWindow = window.open('', '_blank', 'width=900,height=1100');
    if (!printWindow) {
      this.businessCardMessage = 'Allow pop-ups to print this business card sheet.';
      return;
    }

    const title = `${this.businessCardForm.controls.name.value || 'Business Card'} double-sided`;
    const sheetHtml = this.businessCardPrintSheet?.nativeElement.innerHTML || '';
    printWindow.document.open();
    printWindow.document.write(
      this.businessCardPrint.businessCardPrintDocument(title, sheetHtml)
    );
    printWindow.document.close();
    printWindow.focus();
  }

  private openPrintDocument(title: string, html: string): void {
    const printWindow = window.open('', '_blank', 'width=900,height=1100');
    if (!printWindow) {
      this.errorMessage = `Allow pop-ups to print ${title}.`;
      this.businessCardMessage = `Allow pop-ups to print ${title}.`;
      return;
    }

    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();
  }

  async deleteBusinessCard(record: BusinessCardRecord): Promise<void> {
    if (!this.authToken || this.deletingBusinessCardId !== null) {
      return;
    }

    this.deletingBusinessCardId = record.id;
    this.businessCardMessage = '';

    this.http.delete(this.apiV1Url(`business_cards/${record.id}`), {
      headers: this.authHeaders()
    }).subscribe({
      next: () => {
        this.businessCards = this.businessCards.filter((businessCard) => businessCard.id !== record.id);
        this.syncBusinessCardLimitNoticeFromList();
        if (this.generatedBusinessCard?.id === record.id) {
          this.generatedBusinessCard = null;
          this.businessCardQrImageUrl = '';
        }
        this.deletingBusinessCardId = null;
      },
      error: (error) => {
        if (this.handleAuthError(error)) {
          this.deletingBusinessCardId = null;
          return;
        }

        this.businessCardMessage = this.apiErrorMessage(error, 'Unable to delete business card.');
        this.deletingBusinessCardId = null;
      }
    });
  }

  async selectQrCode(record: QrCodeRecord): Promise<void> {
    this.generatedRecord = record;
    this.directQrUrl = '';
    this.activeQrLabelText = record.qr_label_text || '';
    this.activeQrIconSize = record.qr_icon_size;
    this.activeQrLabelSize = record.qr_label_size;
    this.activeQrStickerStyle = record.qr_style;
    await this.setActiveLogo(record.logo_data_url);
    await this.renderQrRecordPreview(record);
  }

  updateStickerSize(value: StickerSizeInches | string): void {
    this.activeQrStickerSize = this.normalizedStickerSize(value);
    this.trimStickerAssignmentsToSheet();
  }

  updateStickerSheetAssignment(change: StickerSheetAssignmentChange): void {
    const nextAssignments = { ...this.stickerSheetAssignments };
    const nextQrCodes = { ...this.stickerSheetQrCodes };

    change.indexes.forEach((index) => {
      if (change.qrCodeId === null) {
        delete nextAssignments[index];
        delete nextQrCodes[index];
      } else {
        nextAssignments[index] = change.qrCodeId;
        if (change.qrCode) {
          nextQrCodes[index] = change.qrCode;
        }
      }
    });

    this.stickerSheetAssignments = nextAssignments;
    this.stickerSheetQrCodes = nextQrCodes;
  }

  stickerSheetAssignmentCount(): number {
    return Object.keys(this.stickerSheetAssignments).length;
  }

  stickerSheetLayout(): StickerSheetLayout {
    const size = this.normalizedStickerSize(this.activeQrStickerSize);
    const columns = Math.max(1, Math.floor(7.5 / size));
    const rows = Math.max(1, Math.floor(10 / size));
    return { columns, rows, count: columns * rows };
  }

  private removeStickerAssignmentsForQrCode(qrCodeId: number): void {
    this.stickerSheetAssignments = Object.fromEntries(
      Object.entries(this.stickerSheetAssignments)
        .filter(([, assignedQrCodeId]) => assignedQrCodeId !== qrCodeId)
    );
    this.stickerSheetQrCodes = Object.fromEntries(
      Object.entries(this.stickerSheetQrCodes)
        .filter(([, assignedQrCode]) => assignedQrCode.id !== qrCodeId)
    );
  }

  private trimStickerAssignmentsToSheet(): void {
    const layout = this.stickerSheetLayout();
    this.stickerSheetAssignments = Object.fromEntries(
      Object.entries(this.stickerSheetAssignments)
        .filter(([positionIndex]) => Number(positionIndex) < layout.count)
    );
    this.stickerSheetQrCodes = Object.fromEntries(
      Object.entries(this.stickerSheetQrCodes)
        .filter(([positionIndex]) => Number(positionIndex) < layout.count)
    );
  }

  async startEditingQrCode(record: QrCodeRecord): Promise<void> {
    if (!this.authToken || this.editingId !== null || this.isSaving) {
      return;
    }

    this.editingRecord = record;
    this.generatedRecord = record;
    this.directQrUrl = '';
    this.editLogoDataUrl = record.logo_data_url || '';
    this.errorMessage = '';
    this.editForm.reset(this.qrFormState.stateFromRecord(record, { authToken: Boolean(this.authToken) }), { emitEvent: false });
    this.editForm.controls.trackAnalytics.disable({ emitEvent: false });
    this.isEditModalOpen = true;
    await this.setActiveLogo(record.logo_data_url);
    await this.renderQrRecordPreview(record);
  }

  cancelEdit(): void {
    this.closeEditModal();
  }

  handleEditModalDismiss(): void {
    if (this.editingRecord && !this.isSaving) {
      this.closeEditModal();
    }
  }

  async updateQrCode(): Promise<void> {
    if (!this.editingRecord || this.editForm.invalid) {
      this.editForm.markAllAsTouched();
      return;
    }

    const record = this.editingRecord;
    this.editingId = record.id;
    this.isSaving = true;
    this.errorMessage = '';
    await this.setActiveLogo(this.editLogoDataUrl || null);

    const value = this.editForm.getRawValue();
    let validatedUrl: string;
    let userAgentRoutes: QrUserAgentRoute[];

    try {
      validatedUrl = await this.validateQrDestinationUrl(value.targetUrl, value.skipResponseValidation);
      userAgentRoutes = await this.validateUserAgentRoutes(
        this.userAgentRoutesInputForSubmit(this.editForm),
        value.skipResponseValidation
      );
    } catch (error) {
      this.errorMessage = this.validationErrorMessage(error);
      this.editingId = null;
      this.isSaving = false;
      return;
    }

    this.http.patch<QrCodeResponse>(this.apiV1Url(`qr_codes/${record.id}`), {
      qr_code: {
        title: value.title,
        target_url: validatedUrl,
        provided_by_page: value.providedByPage,
        qr_style: value.qrStyle,
        qr_label_text: value.qrLabelText || null,
        qr_icon_size: this.normalizedQrIconSize(value.qrIconSize),
        qr_label_size: this.normalizedQrLabelSize(value.qrLabelSize),
        logo_data_url: this.editLogoDataUrl || null,
        user_agent_routes: userAgentRoutes,
        category_names: this.categoryNamesFromInput(value.categoryNames),
        skip_response_validation: value.skipResponseValidation
      }
    }, { headers: this.authHeaders() }).subscribe({
      next: async (response) => {
        this.qrCodes = this.qrCodes.map((qrCode) => (
          qrCode.id === response.data.id ? response.data : qrCode
        ));
        this.stickerSheetQrCodes = Object.fromEntries(
          Object.entries(this.stickerSheetQrCodes).map(([positionIndex, qrCode]) => [
            positionIndex,
            qrCode.id === response.data.id ? response.data : qrCode
          ])
        );
        this.mergeCategoryNames(response.data.category_names);
        this.syncLimitNotice(response.meta);

        this.generatedRecord = response.data;
        this.directQrUrl = '';
        this.activeQrLabelText = response.data.qr_label_text || '';
        this.activeQrIconSize = response.data.qr_icon_size;
        this.activeQrLabelSize = response.data.qr_label_size;
        this.activeQrStickerStyle = response.data.qr_style;
        await this.setActiveLogo(response.data.logo_data_url);
        await this.renderQrRecordPreview(response.data);

        this.closeEditModal();
        this.editingId = null;
        this.isSaving = false;
      },
      error: (error) => {
        if (this.handleAuthError(error)) {
          this.editingId = null;
          this.isSaving = false;
          return;
        }

        this.errorMessage = this.apiErrorMessage(error, 'Unable to update QR code.');
        this.editingId = null;
        this.isSaving = false;
      }
    });
  }

  async deleteQrCode(record: QrCodeRecord): Promise<void> {
    if (!this.authToken || this.deletingId !== null) {
      return;
    }

    const confirmed = await this.confirmDelete(record);
    if (!confirmed) {
      return;
    }

    this.deletingId = record.id;
    this.errorMessage = '';

    this.http.delete(this.apiV1Url(`qr_codes/${record.id}`), {
      headers: this.authHeaders()
    }).subscribe({
      next: () => {
        this.qrCodes = this.qrCodes.filter((qrCode) => qrCode.id !== record.id);
        this.removeStickerAssignmentsForQrCode(record.id);
        this.syncLimitNoticeFromList();

        if (this.generatedRecord?.id === record.id) {
          this.generatedRecord = null;
          this.generatedImageUrl = '';
          this.logoImage = null;
        }

        this.deletingId = null;
      },
      error: (error) => {
        if (this.handleAuthError(error)) {
          this.deletingId = null;
          return;
        }

        this.errorMessage = this.apiErrorMessage(error, 'Unable to delete QR code.');
        this.deletingId = null;
      }
    });
  }

  private async confirmDelete(record: QrCodeRecord): Promise<boolean> {
    const alert = await alertController.create({
      header: 'Delete QR Code',
      message: `Delete ${record.title || record.redirect_path}?`,
      buttons: [
        {
          text: 'Cancel',
          role: 'cancel'
        },
        {
          text: 'Delete',
          role: 'destructive'
        }
      ]
    });

    await alert.present();
    const result = await alert.onDidDismiss();
    return result.role === 'destructive';
  }

  private async confirmDeleteNewsletter(record: NewsletterRecord): Promise<boolean> {
    const alert = await alertController.create({
      header: 'Delete News Letter',
      message: `Delete ${record.title}?`,
      buttons: [
        {
          text: 'Cancel',
          role: 'cancel'
        },
        {
          text: 'Delete',
          role: 'destructive'
        }
      ]
    });

    await alert.present();
    const result = await alert.onDidDismiss();
    return result.role === 'destructive';
  }

  downloadQrCode(): void {
    if (!this.generatedImageUrl) {
      return;
    }

    const anchor = document.createElement('a');
    anchor.href = this.generatedImageUrl;
    anchor.download = `${this.generatedRecord?.title || 'qr-code'}.png`;
    anchor.click();
  }

  async printQrStickerSheet(): Promise<void> {
    if (this.stickerSheetAssignmentCount() > 0) {
      await this.printAssignedQrStickerSheet();
      return;
    }

    const value = this.generatedRecord ? this.redirectUrlFor(this.generatedRecord) : this.directQrUrl;
    if (!value) {
      this.errorMessage = 'Generate a QR code before printing sticker sheets.';
      return;
    }

    const imageUrl = await this.qrStickerImageDataUrl(
      value,
      this.activeQrStickerStyle,
      this.activeQrLabelText,
      this.activeQrIconSize,
      this.activeQrLabelSize,
      this.activeQrStickerSize
    );
    this.openPrintDocument(
      `${this.generatedRecord?.title || 'QR Code'} sticker sheet`,
      this.businessCardPrint.stickerSheetPrintDocument(
        'QR sticker sheet',
        this.businessCardPrint.qrStickerHtml(imageUrl, this.activeQrStickerSize),
        this.activeQrStickerSize
      )
    );
  }

  private async printAssignedQrStickerSheet(): Promise<void> {
    const layout = this.stickerSheetLayout();
    const stickerRecords = Array.from({ length: layout.count }, (_, index) => (
      this.stickerSheetQrCodes[index] || null
    ));
    const stickerHtmlItems = await Promise.all(stickerRecords.map(async (record) => {
      if (!record) {
        return '';
      }

      const imageUrl = await this.qrStickerImageDataUrl(
        this.qrValueForRecord(record),
        record.qr_style,
        record.qr_label_text || '',
        record.qr_icon_size,
        record.qr_label_size,
        this.activeQrStickerSize,
        await this.logoImageForQrCode(record)
      );

      return this.businessCardPrint.qrStickerHtml(imageUrl, this.activeQrStickerSize);
    }));

    this.openPrintDocument(
      'QR sticker sheet',
      this.businessCardPrint.stickerSheetPrintDocument(
        'QR sticker sheet',
        stickerHtmlItems,
        this.activeQrStickerSize
      )
    );
  }

  constructor(
    private readonly http: HttpClient,
    private readonly session: AuthSessionService,
    private readonly nfcWriter: NfcWriterService,
    private readonly qrRender: QrRenderService,
    private readonly businessCardPrint: BusinessCardPrintService,
    private readonly qrFormState: QrFormStateService
  ) {
    window.addEventListener('message', this.handleWindowMessage);
    window.addEventListener('popstate', this.handleWindowPopstate);
  }

  private validToolMode(value: unknown): ToolMode {
    if (value === 'marketingEmail') {
      return this.marketingEmailEnabled ? value : 'qr';
    }

    if (value === 'stickers') {
      return this.authToken ? value : 'qr';
    }

    if (value === 'nfc') {
      return this.canShowNfcTool ? value : 'qr';
    }

    return value === 'businessCard' ? value : 'qr';
  }

  private ensureBusinessCardEditorLoaded(): Promise<void> {
    this.trixLoadPromise ??= Promise.all([
      import('trix'),
      import('@rails/actiontext')
    ]).then(() => {
      configureBusinessCardTrix();
    });

    return this.trixLoadPromise;
  }

  private scheduleBusinessCardTrixSync(): void {
    if (this.selectedTool !== 'businessCard' || this.businessCardTrixSyncQueued) {
      return;
    }

    this.businessCardTrixSyncQueued = true;
    window.setTimeout(() => {
      this.businessCardTrixSyncQueued = false;
      void this.ensureBusinessCardEditorLoaded().then(() => this.syncVisibleBusinessCardTrixEditors(true));
    });
    window.setTimeout(() => this.syncVisibleBusinessCardTrixEditors(true), 80);
    window.setTimeout(() => this.syncVisibleBusinessCardTrixEditors(true), 250);
  }

  private syncVisibleBusinessCardTrixEditors(force = false): void {
    this.businessCardTextSectionsForSelectedSide().forEach((section) => {
      this.syncBusinessCardTextEditor(section.id, force);
    });
  }

  private waitForNextRenderTurn(): Promise<void> {
    return new Promise((resolve) => requestAnimationFrame(() => resolve()));
  }

  private registerAppUrlOpenListener(): void {
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    void App.addListener('appUrlOpen', (event: URLOpenListenerEvent) => {
      this.handleOAuthCallbackUrl(event.url);
    }).then((listener) => {
      this.appUrlOpenListener = listener;
    });

    void App.getLaunchUrl().then((launchUrl) => {
      if (launchUrl?.url) {
        this.handleOAuthCallbackUrl(launchUrl.url);
      }
    });
  }

  private async startNativeOAuth(provider: OAuthProvider): Promise<void> {
    try {
      const authUrl = await this.oauthLoginUrl(
        new HttpParams()
          .set('provider', provider)
          .set('app_callback_url', OAUTH_APP_CALLBACK_URL)
      );
      await Browser.open({ url: authUrl.toString() });
    } catch (error) {
      this.clearAuthSession(this.apiErrorMessage(error, 'Unable to start sign-in.'));
    }
  }

  private async oauthLoginUrl(params: HttpParams): Promise<URL> {
    const response = await firstValueFrom(
      this.http.get<OAuthLoginResponse>(this.apiUrl('/api/users/login'), { params })
    );

    return new URL(response.auth_url, this.apiBaseUrl());
  }

  private handleOAuthCallbackUrl(url: string): void {
    let callbackUrl: URL;

    try {
      callbackUrl = new URL(url);
    } catch {
      return;
    }

    if (!this.isOAuthCallbackUrl(callbackUrl)) {
      return;
    }

    if (Capacitor.isNativePlatform()) {
      void Browser.close().catch(() => undefined);
    }

    const payload = this.oauthCallbackPayload(callbackUrl);
    if (!payload) {
      return;
    }

    try {
      const oauthPayload = JSON.parse(payload);
      this.handleOAuthPayload(oauthPayload);
      if (window.opener) {
        window.opener.postMessage(oauthPayload, window.location.origin);
        window.close();
      }
    } catch {
      this.clearAuthSession('Unable to authenticate. Sign in with Google to continue.');
    }
  }

  private handleOAuthPayload(payload: unknown): void {
    if (!payload || typeof payload !== 'object') {
      return;
    }

    const oauthPayload = payload as { type?: string; token?: string; message?: string };
    if (oauthPayload.type === 'oauth:success' && oauthPayload.token) {
      this.storeAuth(oauthPayload as AuthResponse);
    } else if (oauthPayload.type === 'oauth:error') {
      this.clearAuthSession(oauthPayload.message || 'Unable to authenticate. Sign in with Google to continue.');
    }
  }

  private isOAuthCallbackUrl(callbackUrl: URL): boolean {
    const callbackPath = this.normalizeCallbackPath(callbackUrl.pathname);

    if (callbackUrl.origin === window.location.origin && callbackPath === WEB_OAUTH_CALLBACK_PATH) {
      return true;
    }

    return OAUTH_APP_CALLBACK_URLS.some((allowedCallbackUrl) => {
      const allowedUrl = new URL(allowedCallbackUrl);
      const allowedPath = this.normalizeCallbackPath(allowedUrl.pathname);

      return callbackUrl.protocol === allowedUrl.protocol &&
        callbackUrl.host === allowedUrl.host &&
        callbackPath === allowedPath;
    });
  }

  private oauthCallbackPayload(callbackUrl: URL): string | null {
    const queryPayload = callbackUrl.searchParams.get('payload');
    if (queryPayload) {
      return queryPayload;
    }

    if (!callbackUrl.hash) {
      return null;
    }

    const hash = callbackUrl.hash.slice(1);
    const hashQuery = hash.startsWith('?') ? hash.slice(1) : hash;
    return new URLSearchParams(hashQuery).get('payload');
  }

  private normalizeCallbackPath(pathname: string): string {
    const normalizedPath = pathname.replace(/\/+$/, '');
    return normalizedPath || WEB_OAUTH_CALLBACK_PATH;
  }

  private shouldUseNativeOAuth(): boolean {
    if (Capacitor.isNativePlatform()) {
      return true;
    }

    return window.location.protocol === 'https:' &&
      ['localhost', 'qrcodes.capacitor.local'].includes(window.location.hostname);
  }

  private isAndroidMobile(): boolean {
    return Capacitor.getPlatform() === 'android' || /Android/i.test(navigator.userAgent);
  }

  private webOAuthCallbackUrl(): string {
    return window.location.origin;
  }

  private async generateDirectQr(targetUrl: string): Promise<void> {
    const styleId = this.form.controls.qrStyle.value;
    this.directQrUrl = targetUrl;
    await this.renderQrPreview(targetUrl, styleId, this.activeQrLabelText, this.activeQrIconSize, this.activeQrLabelSize);
    this.resetFormForReuse();
    this.isSaving = false;
  }

  private qrImportPayloadsFromText(importText: string): QrCodeImportPayload[] {
    const parsedRows = this.parseCsvRows(importText)
      .filter((row) => row.some((cell) => cell.trim().length > 0));
    if (parsedRows.length === 0) {
      return [];
    }

    const header = this.qrImportHeader(parsedRows[0]);
    const dataRows = header ? parsedRows.slice(1) : parsedRows;

    return dataRows
      .map((row) => this.qrImportPayloadFromRow(row, header))
      .filter((payload) => payload.target_url.trim().length > 0)
      .slice(0, 100);
  }

  private qrImportPayloadFromRow(row: string[], header: Record<string, number> | null): QrCodeImportPayload {
    const value = this.form.getRawValue();
    const title = this.importCell(row, header, ['title', 'name'], 0);
    const targetUrl = header
      ? this.importCell(row, header, ['target_url', 'target url', 'url', 'destination'])
      : (row.length === 1 ? row[0] : row[1]);
    const categories = this.importCell(row, header, ['category_names', 'categories', 'category'], 2);
    const qrStyle = this.normalizeQrStyle(this.importCell(row, header, ['qr_style', 'style']) || value.qrStyle);

    return {
      title,
      target_url: targetUrl,
      track_analytics: this.importBooleanCell(row, header, ['track_analytics', 'tracked'], value.trackAnalytics),
      provided_by_page: this.importBooleanCell(row, header, ['provided_by_page', 'provided by page'], value.providedByPage),
      qr_style: qrStyle,
      qr_label_text: this.importCell(row, header, ['qr_label_text', 'label']) || value.qrLabelText || null,
      qr_icon_size: this.normalizedQrIconSize(this.importCell(row, header, ['qr_icon_size', 'icon size']) || value.qrIconSize),
      qr_label_size: this.normalizedQrLabelSize(this.importCell(row, header, ['qr_label_size', 'label size']) || value.qrLabelSize),
      category_names: this.categoryNamesFromInput(categories || value.categoryNames),
      skip_response_validation: this.importBooleanCell(
        row,
        header,
        ['skip_response_validation', 'skip response validation'],
        value.skipResponseValidation
      )
    };
  }

  private parseCsvRows(value: string): string[][] {
    const rows: string[][] = [];
    let row: string[] = [];
    let cell = '';
    let quoted = false;

    for (let index = 0; index < value.length; index += 1) {
      const character = value[index];
      const nextCharacter = value[index + 1];

      if (character === '"' && quoted && nextCharacter === '"') {
        cell += '"';
        index += 1;
      } else if (character === '"') {
        quoted = !quoted;
      } else if (character === ',' && !quoted) {
        row.push(cell.trim());
        cell = '';
      } else if ((character === '\n' || character === '\r') && !quoted) {
        if (character === '\r' && nextCharacter === '\n') {
          index += 1;
        }
        row.push(cell.trim());
        rows.push(row);
        row = [];
        cell = '';
      } else {
        cell += character;
      }
    }

    row.push(cell.trim());
    rows.push(row);

    return rows;
  }

  private qrImportHeader(row: string[]): Record<string, number> | null {
    const aliases = ['target_url', 'target url', 'url', 'destination'];
    const normalizedCells = row.map((cell) => cell.trim().toLowerCase());
    if (!normalizedCells.some((cell) => aliases.includes(cell))) {
      return null;
    }

    return Object.fromEntries(normalizedCells.map((cell, index) => [cell, index]));
  }

  private importCell(row: string[], header: Record<string, number> | null, keys: string[], fallbackIndex?: number): string {
    if (header) {
      const index = keys.map((key) => header[key]).find((candidate) => candidate !== undefined);
      return index === undefined ? '' : row[index]?.trim() || '';
    }

    return fallbackIndex === undefined ? '' : row[fallbackIndex]?.trim() || '';
  }

  private importBooleanCell(
    row: string[],
    header: Record<string, number> | null,
    keys: string[],
    fallback: boolean
  ): boolean {
    const value = this.importCell(row, header, keys).toLowerCase();
    if (!value) {
      return fallback;
    }

    return ['1', 'true', 'yes', 'y'].includes(value);
  }

  private normalizeQrStyle(value: string): QrStyleId {
    return this.qrStyles.find((style) => style.id === value)?.id || 'classic';
  }

  private async validateTargetUrl(targetUrl: string, skipResponseValidation = false): Promise<string> {
    const response = await firstValueFrom(
      this.http.post<UrlValidationResponse>(this.apiV1Url('url_validation'), {
        url: targetUrl,
        skip_response_validation: skipResponseValidation
      }, { headers: skipResponseValidation ? this.authHeaders() : undefined })
    );

    return response.data.url;
  }

  private async validateQrDestinationUrl(targetUrl: string, skipResponseValidation = false): Promise<string> {
    const normalizedUrl = targetUrl.trim();

    if (!this.hasUrlScheme(normalizedUrl) || this.isWebUrl(normalizedUrl)) {
      return this.validateTargetUrl(normalizedUrl, skipResponseValidation);
    }

    if (!this.isAllowedDeepLinkUrl(normalizedUrl)) {
      throw new Error('Enter a valid website URL or deep link.');
    }

    return normalizedUrl;
  }

  private async validateUserAgentRoutes(value: string, skipResponseValidation = false): Promise<QrUserAgentRoute[]> {
    const routes = this.qrFormState.routesFromInput(value);
    const validatedRoutes: QrUserAgentRoute[] = [];

    for (const route of routes) {
      validatedRoutes.push({
        patterns: route.patterns,
        target_url: await this.validateQrDestinationUrl(route.target_url, skipResponseValidation),
        ...(route.redirect_percentage ? { redirect_percentage: route.redirect_percentage } : {}),
        ...(route.every_nth_scan ? { every_nth_scan: route.every_nth_scan } : {})
      });
    }

    return validatedRoutes;
  }

  private userAgentRouteLines(form: FormGroup): string[] {
    return String(form.controls['userAgentRoutes']?.value || '')
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter((line) => line.length > 0);
  }

  private userAgentRoutesInputForSubmit(form: FormGroup): string {
    const stagedRouteLine = this.userAgentRouteLineFromForm(form);
    if (stagedRouteLine === null) {
      throw new Error('User agent routes must use either redirect percentage or every nth scan, not both.');
    }

    if (!stagedRouteLine) {
      return this.userAgentRouteLines(form).join('\n');
    }

    const stagedRoutePreset = this.userAgentRoutePresetFromForm(form);
    const existingLines = this.userAgentRouteLines(form);
    const lines = stagedRoutePreset
      ? existingLines.filter((line) => !this.qrFormState.routeMatchesPreset(line, stagedRoutePreset))
      : existingLines;

    return [...lines, stagedRouteLine].join('\n');
  }

  private hasUrlScheme(targetUrl: string): boolean {
    return /^[a-z][a-z0-9+.-]*:/i.test(targetUrl);
  }

  private isWebUrl(targetUrl: string): boolean {
    return /^https?:\/\//i.test(targetUrl);
  }

  private isAllowedDeepLinkUrl(targetUrl: string): boolean {
    const blockedSchemes = new Set(['data:', 'file:', 'javascript:', 'vbscript:']);

    try {
      const url = new URL(targetUrl);
      return !blockedSchemes.has(url.protocol) && Boolean(url.hostname || url.pathname);
    } catch {
      return false;
    }
  }

  private validationErrorMessage(error: unknown): string {
    if (this.failedRequestUrl(error)) {
      return this.apiErrorMessage(error, 'Destination must be a valid website URL or deep link.');
    }

    if (error && typeof error === 'object' && 'error' in error) {
      const responseError = (error as { error?: { error?: string } }).error;
      if (responseError?.error) {
        return responseError.error;
      }
    }

    if (error instanceof Error && error.message) {
      return error.message;
    }

    return 'Destination must be a valid website URL or deep link.';
  }

  private errorMessages(error: unknown): string {
    if (!error || typeof error !== 'object' || !('error' in error)) {
      return '';
    }

    const responseError = (error as { error?: unknown }).error;
    if (responseError && typeof responseError === 'object') {
      const messages = responseError as { errors?: string[]; error?: string };
      return messages.errors?.join(', ') || messages.error || '';
    }

    return '';
  }

  private apiErrorMessage(error: unknown, fallback: string): string {
    const message = this.errorMessages(error) || fallback;
    const url = this.failedRequestUrl(error);

    return url ? `${message} Request URL: ${url}` : message;
  }

  private failedRequestUrl(error: unknown): string {
    if (!error || typeof error !== 'object' || !('url' in error)) {
      return '';
    }

    const url = (error as { url?: unknown }).url;
    return typeof url === 'string' ? url : '';
  }

  private storeAuth(response: AuthResponse): void {
    this.session.storeAuth(response);
    this.authToken = this.session.token;
    this.currentEmail = this.session.email;
    console.info('[Wahl Auth] OAuth session stored', {
      tokenStored: Boolean(this.authToken),
      email: this.currentEmail
    });
    this.authMessage = `Signed in as ${response.user.email}.`;
    this.syncTrackedAnalyticsControl();
    this.syncBusinessCardTrackedAnalyticsControl();
    console.info('[Wahl Auth] Loading authenticated account data');
    this.loadAccountContext();
    this.loadQrCodes();
    this.loadCurrentUserPreferences();
    this.loadCategories();
    this.onSelectedToolChange(this.selectedTool);
  }

  private upsertQrCode(record: QrCodeRecord): void {
    this.qrCodes = [
      record,
      ...this.qrCodes.filter((qrCode) => qrCode.id !== record.id)
    ];
  }

  categorySuggestions(): string[] {
    const savedNames = this.categories.map((category) => category.name);
    const usedNames = [
      ...this.qrCodes.flatMap((record) => record.category_names),
      ...this.businessCards.flatMap((record) => record.category_names),
      ...this.newsletters.flatMap((record) => record.category_names)
    ];

    return this.uniqueCategoryNames([
      ...savedNames,
      ...usedNames,
      ...this.defaultCategoryNames
    ]).slice(0, 24);
  }

  selectedCategoryNames(form: FormGroup): string[] {
    return this.categoryNamesFromInput(String(form.controls['categoryNames']?.value || ''));
  }

  categorySelected(form: FormGroup, categoryName: string): boolean {
    return this.selectedCategoryNames(form).some((name) => name.toLowerCase() === categoryName.toLowerCase());
  }

  toggleCategoryInForm(form: FormGroup, categoryName: string): void {
    const control = form.controls['categoryNames'];
    if (!control) {
      return;
    }

    const names = this.categoryNamesFromInput(String(control.value || ''));
    const alreadySelected = names.some((name) => name.toLowerCase() === categoryName.toLowerCase());
    const nextNames = alreadySelected
      ? names.filter((name) => name.toLowerCase() !== categoryName.toLowerCase())
      : [...names, categoryName];

    control.setValue(nextNames.join(', '));
    control.markAsDirty();
  }

  addUserAgentRouteToForm(form: FormGroup): void {
    if (!this.authToken) {
      return;
    }

    const routeLine = this.userAgentRouteLineFromForm(form);
    const preset = this.userAgentRoutePresetFromForm(form);

    if (routeLine === null) {
      this.errorMessage = 'Use either redirect percentage or every nth scan, not both.';
      return;
    }

    if (!preset || !routeLine) {
      this.errorMessage = 'Choose a user agent and add a route destination.';
      return;
    }

    const existingLines = this.userAgentRouteLines(form)
      .filter((line) => !this.qrFormState.routeMatchesPreset(line, preset));
    const control = form.controls['userAgentRoutes'];

    control?.setValue([...existingLines, routeLine].join('\n'));
    control?.markAsDirty();
    form.controls['userAgentPreset']?.setValue('');
    form.controls['userAgentTargetUrl']?.setValue('');
    form.controls['userAgentRedirectPercentage']?.setValue('');
    form.controls['userAgentEveryNthScan']?.setValue('');
    this.errorMessage = '';
  }

  syncUserAgentFrequencyChoice(form: FormGroup, mode: 'percentage' | 'nth'): void {
    const sourceControlName = mode === 'percentage' ? 'userAgentRedirectPercentage' : 'userAgentEveryNthScan';
    const targetControlName = mode === 'percentage' ? 'userAgentEveryNthScan' : 'userAgentRedirectPercentage';
    const sourceValue = String(form.controls[sourceControlName]?.value || '').trim();

    if (sourceValue) {
      form.controls[targetControlName]?.setValue('');
    }
  }

  removeUserAgentRouteFromForm(form: FormGroup, routeIndex: number): void {
    const control = form.controls['userAgentRoutes'];
    if (!control) {
      return;
    }

    const lines = this.userAgentRouteLines(form);
    lines.splice(routeIndex, 1);
    control.setValue(lines.join('\n'));
    control.markAsDirty();
  }

  userAgentRouteSummaries(form: FormGroup): string[] {
    try {
      return this.qrFormState.routesFromInput(String(form.controls['userAgentRoutes']?.value || ''))
        .map((route) => `${this.qrFormState.routeLabel(route.patterns)} -> ${route.target_url}${this.qrFormState.routeFrequencyLabel(route)}`);
    } catch {
      return this.userAgentRouteLines(form);
    }
  }

  private userAgentRouteLineFromForm(form: FormGroup): string | null | undefined {
    return this.qrFormState.routeLineFromDraft({
      userAgentPreset: String(form.controls['userAgentPreset']?.value || ''),
      userAgentTargetUrl: String(form.controls['userAgentTargetUrl']?.value || ''),
      userAgentEveryNthScan: String(form.controls['userAgentEveryNthScan']?.value || ''),
      userAgentRedirectPercentage: String(form.controls['userAgentRedirectPercentage']?.value || '')
    });
  }

  private userAgentRoutePresetFromForm(form: FormGroup): QrUserAgentRoutePreset | undefined {
    const presetId = String(form.controls['userAgentPreset']?.value || '');

    return this.qrFormState.presetForId(presetId);
  }

  private upsertNfcTag(record: NfcTagRecord): void {
    this.nfcTags = [
      record,
      ...this.nfcTags.filter((nfcTag) => nfcTag.id !== record.id)
    ];
  }

  private upsertBusinessCard(record: BusinessCardRecord): void {
    this.businessCards = [
      record,
      ...this.businessCards.filter((businessCard) => businessCard.id !== record.id)
    ];
  }

  private upsertNewsletter(record: NewsletterRecord): void {
    this.newsletters = [
      record,
      ...this.newsletters.filter((newsletter) => newsletter.id !== record.id)
    ];
  }

  private newsletterPayload(): NewsletterPayload {
    const value = this.newsletterForm.getRawValue();
    const snapshot = this.newsletterEditor?.snapshot() || { content: {}, html: '', css: '' };

    return {
      title: value.title,
      content: snapshot.content,
      html: snapshot.html,
      css: snapshot.css,
      category_names: this.categoryNamesFromInput(value.categoryNames)
    };
  }

  private businessCardPayload(): BusinessCardPayload {
    this.syncBusinessCardFormFromContent();
    const value = this.businessCardForm.getRawValue();

    return {
      name: value.name,
      job_title: value.jobTitle,
      company: value.company,
      email: value.email,
      phone: value.phone,
      website: value.website,
      address: value.address,
      tagline: value.tagline,
      accent_color: this.businessCardAccentColor(),
      qr_code_id: this.businessCardQrCodes.find((qrCode) => qrCode.id > 0)?.id || null,
      qr_code_ids: this.businessCardQrCodes.filter((qrCode) => qrCode.id > 0).map((qrCode) => qrCode.id),
      card_layout: this.businessCardLayout,
      card_content: this.businessCardContent,
      category_names: this.categoryNamesFromInput(value.categoryNames)
    };
  }

  private categoryNamesFromInput(value: string): string[] {
    return value
      .split(',')
      .map((name) => name.trim())
      .filter((name, index, names) => name.length > 0 && names.indexOf(name) === index)
      .slice(0, 20);
  }

  private mergeCategoryNames(categoryNames: string[]): void {
    const existingNames = new Set(this.categories.map((category) => category.name.toLowerCase()));
    const nextCategories = [...this.categories];

    this.uniqueCategoryNames(categoryNames).forEach((name) => {
      if (existingNames.has(name.toLowerCase())) {
        return;
      }

      existingNames.add(name.toLowerCase());
      nextCategories.push({
        id: 0,
        name,
        created_at: new Date().toISOString()
      });
    });

    this.categories = nextCategories.sort((first, second) => first.name.localeCompare(second.name));
  }

  private uniqueCategoryNames(categoryNames: string[]): string[] {
    const seen = new Set<string>();

    return categoryNames
      .map((name) => name.trim())
      .filter((name) => {
        const key = name.toLowerCase();
        if (!key || seen.has(key)) {
          return false;
        }

        seen.add(key);
        return true;
      });
  }

  private previewBusinessCardRecord(): BusinessCardRecord {
    this.syncBusinessCardFormFromContent();
    const value = this.businessCardForm.getRawValue();

    return {
      id: 0,
      name: value.name,
      job_title: value.jobTitle || null,
      company: value.company || null,
      email: value.email || null,
      phone: value.phone || null,
      website: value.website || null,
      address: value.address || null,
      tagline: value.tagline || null,
      accent_color: this.businessCardAccentColor(),
      card_layout: this.businessCardLayout,
      card_content: this.businessCardContent,
      qr_code_id: this.businessCardQrCodes.find((qrCode) => qrCode.id > 0)?.id || null,
      qr_code: this.businessCardQrCodes.find((qrCode) => qrCode.id > 0) || null,
      qr_code_ids: this.businessCardQrCodes.filter((qrCode) => qrCode.id > 0).map((qrCode) => qrCode.id),
      qr_codes: this.businessCardQrCodes.filter((qrCode) => qrCode.id > 0),
      category_ids: [],
      category_names: this.categoryNamesFromInput(value.categoryNames),
      categories: [],
      created_at: new Date().toISOString()
    };
  }

  private async attachQrCodeToBusinessCard(qrCode: QrCodeRecord): Promise<void> {
    const card = this.generatedBusinessCard;

    if (!card || !this.authToken || card.id === 0) {
      this.generatedBusinessCard = {
        ...this.previewBusinessCardRecord(),
        qr_code_id: this.businessCardQrCodes.find((record) => record.id > 0)?.id || qrCode.id,
        qr_code: this.businessCardQrCodes.find((record) => record.id > 0) || qrCode,
        qr_code_ids: this.businessCardQrCodes.filter((record) => record.id > 0).map((record) => record.id),
        qr_codes: this.businessCardQrCodes.filter((record) => record.id > 0)
      };
      return;
    }

    const response = await firstValueFrom(
      this.http.patch<BusinessCardResponse>(this.apiV1Url(`business_cards/${card.id}`), {
        business_card: {
          ...this.businessCardPayload(),
          qr_code_id: this.businessCardQrCodes.find((record) => record.id > 0)?.id || qrCode.id,
          qr_code_ids: this.businessCardQrCodes.filter((record) => record.id > 0).map((record) => record.id)
        }
      }, { headers: this.authHeaders() })
    );
    this.generatedBusinessCard = response.data;
    this.mergeCategoryNames(response.data.category_names);
    this.upsertBusinessCard(response.data);
  }

  private async syncBusinessCardQrPreview(record: BusinessCardRecord): Promise<void> {
    const qrCodes = this.businessCardQrCodesFor(record);
    this.businessCardQrCodes = qrCodes;

    if (qrCodes.length === 0) {
      this.businessCardQrImages = {};
      this.businessCardQrImageUrl = '';
      return;
    }

    const images: Record<number, string> = {};
    for (const qrCode of qrCodes) {
      images[qrCode.id] = await this.qrImageDataUrlForRecord(qrCode);
      if (!this.qrCodeHasLayoutOnAnySide(qrCode)) {
        this.ensureQrLayout(qrCode.id);
      }
    }
    this.businessCardQrImages = images;
    this.businessCardQrImageUrl = images[qrCodes[0].id] || '';
  }

  private defaultBusinessCardLayout(): BusinessCardLayout {
    return {
      front: {
        'text-front-1': { x: 6, y: 24 }
      },
      back: {
        'text-back-1': { x: 6, y: 24 }
      }
    };
  }

  private defaultBusinessCardContent(): BusinessCardContent {
    return {
      front: [
        {
          id: 'text-front-1',
          type: 'text',
          text: '',
          html: '',
          size: 'normal',
          color: this.businessCardDefaultTextColor()
        }
      ],
      back: [
        {
          id: 'text-back-1',
          type: 'text',
          text: '',
          html: '',
          size: 'normal',
          color: this.businessCardDefaultTextColor()
        }
      ],
      background: {
        front: this.defaultBusinessCardBackground(),
        back: this.defaultBusinessCardBackground()
      }
    };
  }

  private normalizedBusinessCardLayout(layout?: BusinessCardLayoutInput | null): BusinessCardLayout {
    const defaults = this.defaultBusinessCardLayout();

    if (!layout || typeof layout !== 'object') {
      return defaults;
    }

    if ('front' in layout || 'back' in layout) {
      const sidedLayout = layout as Record<string, BusinessCardSideLayoutInput>;
      return {
        front: this.normalizedSideLayout(sidedLayout['front'], defaults.front),
        back: this.normalizedSideLayout(sidedLayout['back'], defaults.back)
      };
    }

    return {
      front: this.normalizedSideLayout(layout as BusinessCardSideLayoutInput, defaults.front),
      back: defaults.back
    };
  }

  private normalizedSideLayout(layout: BusinessCardSideLayoutInput | undefined, defaults: BusinessCardSideLayout): BusinessCardSideLayout {
    const normalized = { ...defaults };
    if (!layout || typeof layout !== 'object') {
      return normalized;
    }

    Object.entries(layout).forEach(([elementId, position]) => {
      normalized[elementId] = {
        x: this.clampPercent(Number(position?.x ?? defaults[elementId]?.x ?? 0)),
        y: this.clampPercent(Number(position?.y ?? defaults[elementId]?.y ?? 0)),
        size: this.normalizedBusinessCardQrCodeSize(position?.size ?? defaults[elementId]?.size ?? 22)
      };
    });

    return normalized;
  }

  private normalizedBusinessCardContent(content?: BusinessCardContentInput | null, fallbackRecord?: BusinessCardRecord): BusinessCardContent {
    if (!content || typeof content !== 'object') {
      return fallbackRecord ? this.businessCardContentFromLegacyRecord(fallbackRecord) : this.defaultBusinessCardContent();
    }

    if (!Array.isArray(content.front) && !Array.isArray(content.back) && fallbackRecord) {
      return this.businessCardContentFromLegacyRecord(fallbackRecord);
    }

    return {
      front: this.normalizedTextSections(content.front, this.defaultBusinessCardContent().front),
      back: this.normalizedTextSections(content.back, this.defaultBusinessCardContent().back),
      background: this.normalizedBusinessCardBackgrounds(content.background)
    };
  }

  private defaultBusinessCardBackground(): BusinessCardBackground {
    return {
      color: '#164c63',
      imageDataUrl: '',
      fade: 85,
      style: 'linear'
    };
  }

  private normalizedBusinessCardBackgrounds(value: unknown): Record<BusinessCardSide, BusinessCardBackground> {
    const defaults = {
      front: this.defaultBusinessCardBackground(),
      back: this.defaultBusinessCardBackground()
    };

    if (!value || typeof value !== 'object') {
      return defaults;
    }

    const background = value as Record<string, unknown>;
    return {
      front: this.normalizedBusinessCardBackground(background['front'], defaults.front),
      back: this.normalizedBusinessCardBackground(background['back'], defaults.back)
    };
  }

  private normalizedBusinessCardBackground(value: unknown, fallback: BusinessCardBackground): BusinessCardBackground {
    if (!value || typeof value !== 'object') {
      return fallback;
    }

    const background = value as Partial<Record<keyof BusinessCardBackground, unknown>>;
    return {
      color: this.normalizeHexColor(background.color, fallback.color),
      imageDataUrl: typeof background.imageDataUrl === 'string' && background.imageDataUrl.startsWith('data:image/')
        ? background.imageDataUrl
        : '',
      fade: this.clampBackgroundFade(Number(background.fade ?? fallback.fade)),
      style: this.normalizeBusinessCardBackgroundStyle(background.style, fallback.style)
    };
  }

  private normalizedTextSections(value: unknown, fallback: BusinessCardContentItem[]): BusinessCardContentItem[] {
    if (!Array.isArray(value)) {
      return fallback;
    }

    const sections = value
      .filter((section): section is Partial<BusinessCardContentItem> => Boolean(section) && typeof section === 'object')
      .map((section, index): BusinessCardContentItem => {
        const id = typeof section.id === 'string' && section.id ? section.id : this.newBusinessCardTextSectionId(index);
        if (section.type === 'logo' && typeof section.dataUrl === 'string' && section.dataUrl) {
          return {
            id,
            type: 'logo',
            dataUrl: section.dataUrl
          };
        }

        return {
          id,
          type: 'text',
          text: typeof section.text === 'string' ? section.text : this.businessCardPlainTextFromHtml(typeof section.html === 'string' ? section.html : ''),
          html: this.sanitizeBusinessCardRichText(typeof section.html === 'string' ? section.html : this.businessCardTextToHtml(typeof section.text === 'string' ? section.text : '')),
          size: this.normalizedBusinessCardTextSize(section.size),
          color: this.normalizeHexColor(section.color, this.businessCardDefaultTextColor())
        };
      });

    return sections.length > 0 ? sections : fallback;
  }

  private businessCardContentFromLegacyRecord(record: BusinessCardRecord): BusinessCardContent {
    const frontText = [
      record.company,
      record.name,
      record.job_title
    ].filter(Boolean).join('\n');
    const backText = [
      record.email,
      record.phone,
      record.website,
      record.address,
      record.tagline
    ].filter(Boolean).join('\n');

    return {
      front: [{
        id: 'text-front-1',
        type: 'text',
        text: frontText || record.name || '',
        html: this.businessCardTextToHtml(frontText || record.name || ''),
        size: 'normal',
        color: this.businessCardDefaultTextColor()
      }],
      back: [{
        id: 'text-back-1',
        type: 'text',
        text: backText,
        html: this.businessCardTextToHtml(backText),
        size: 'normal',
        color: this.businessCardDefaultTextColor()
      }],
      background: {
        front: {
          ...this.defaultBusinessCardBackground(),
          color: this.normalizeHexColor(record.accent_color, '#164c63')
        },
        back: this.defaultBusinessCardBackground()
      }
    };
  }

  private positionForElement(elementId: BusinessCardElementId): BusinessCardLayoutPosition {
    return this.positionForElementOnSide(this.selectedBusinessCardSide, elementId);
  }

  private positionForElementOnSide(side: BusinessCardSide, elementId: BusinessCardElementId): BusinessCardLayoutPosition {
    return this.businessCardLayout[side][elementId] || { x: 0, y: 0 };
  }

  private ensureQrLayout(qrCodeId: number): void {
    const elementId = this.qrElementId({ id: qrCodeId } as QrCodeRecord);
    const sideLayout = this.businessCardLayout[this.selectedBusinessCardSide];
    if (sideLayout[elementId]) {
      return;
    }

    const qrCountOnSide = Object.keys(sideLayout).filter((key) => key.startsWith('qr-')).length;
    this.businessCardLayout = {
      ...this.businessCardLayout,
      [this.selectedBusinessCardSide]: {
        ...sideLayout,
        [elementId]: {
          x: this.clampPercent(68 - (qrCountOnSide % 2) * 18),
          y: this.clampPercent(18 + Math.floor(qrCountOnSide / 2) * 30),
          size: 22
        }
      }
    };
  }

  private ensureTextSectionLayout(sectionId: string, side: BusinessCardSide): void {
    const sideLayout = this.businessCardLayout[side];
    if (sideLayout[sectionId]) {
      return;
    }

    const sectionCount = this.businessCardContent[side].length;
    this.businessCardLayout = {
      ...this.businessCardLayout,
      [side]: {
        ...sideLayout,
        [sectionId]: {
          x: 6,
          y: this.clampPercent(18 + Math.max(sectionCount - 1, 0) * 18)
        }
      }
    };
  }

  private ensureLogoSectionLayout(sectionId: string, side: BusinessCardSide): void {
    const sideLayout = this.businessCardLayout[side];
    if (sideLayout[sectionId]) {
      return;
    }

    const logoCount = this.businessCardContent[side].filter((section) => section.type === 'logo').length;
    this.businessCardLayout = {
      ...this.businessCardLayout,
      [side]: {
        ...sideLayout,
        [sectionId]: {
          x: this.clampPercent(68 - ((logoCount - 1) % 2) * 18),
          y: this.clampPercent(56 + Math.floor((logoCount - 1) / 2) * 18)
        }
      }
    };
  }

  qrElementId(qrCode: QrCodeRecord): string {
    return `qr-${qrCode.id}`;
  }

  businessCardQrCodesForSelectedSide(): QrCodeRecord[] {
    return this.businessCardQrCodesForSide(this.selectedBusinessCardSide);
  }

  businessCardQrCodesForSide(side: BusinessCardSide): QrCodeRecord[] {
    const sideLayout = this.businessCardLayout[side];
    return this.businessCardQrCodes.filter((qrCode) => Boolean(sideLayout[this.qrElementId(qrCode)]));
  }

  businessCardNfcTargetLabel(): string {
    if (this.businessCardNfcPayloadMode === 'vcard') {
      const name = this.businessCardForm.controls.name.value.trim();
      return name ? `vCard for ${name}` : 'vCard from entered fields';
    }

    return this.businessCardNfcTargetUrl() || 'No URL selected';
  }

  private syncBusinessCardFormFromContent(overwriteExisting = true): void {
    const allLines = [
      ...this.businessCardContent.front,
      ...this.businessCardContent.back
    ].filter((section): section is BusinessCardTextSection => section.type === 'text')
      .flatMap((section) => this.businessCardPlainText(section).split(/\r?\n/).map((line) => line.trim()).filter(Boolean));
    const firstLine = allLines[0] || '';
    const detectedUrl = allLines.find((line) => /^https?:\/\//i.test(line)) || '';

    const updates: Partial<{ name: string; website: string }> = {};

    if ((overwriteExisting || !this.businessCardForm.controls.name.value.trim()) && firstLine) {
      updates.name = firstLine.slice(0, 120);
    }

    if ((overwriteExisting || !this.businessCardForm.controls.website.value.trim()) && detectedUrl) {
      updates.website = detectedUrl;
    }

    if (Object.keys(updates).length > 0) {
      this.businessCardForm.patchValue(updates, { emitEvent: false });
    }
  }

  private newBusinessCardTextSectionId(seed = Date.now()): string {
    return `text-${this.selectedBusinessCardSide}-${seed}-${Math.random().toString(36).slice(2, 8)}`;
  }

  private newBusinessCardLogoSectionId(seed = Date.now()): string {
    return `logo-${this.selectedBusinessCardSide}-${seed}-${Math.random().toString(36).slice(2, 8)}`;
  }

  private businessCardPlainText(section: BusinessCardTextSection): string {
    return section.text || this.businessCardPlainTextFromHtml(section.html || '');
  }

  private normalizedBusinessCardTextSize(value: unknown): BusinessCardTextSize {
    return value === 'small' || value === 'large' || value === 'xlarge' ? value : 'normal';
  }

  private businessCardTextSizeFontSize(value: unknown): string {
    switch (this.normalizedBusinessCardTextSize(value)) {
      case 'small':
        return '0.85em';
      case 'large':
        return '1.2em';
      case 'xlarge':
        return '1.45em';
      default:
        return '1em';
    }
  }

  private businessCardPlainTextFromHtml(html: string): string {
    if (!html) {
      return '';
    }

    const doc = document.implementation.createHTMLDocument('');
    doc.body.innerHTML = this.sanitizeBusinessCardRichText(html);
    doc.body.querySelectorAll('div, p, h1, blockquote, li, br').forEach((element) => {
      element.appendChild(doc.createTextNode('\n'));
    });

    return (doc.body.textContent || '').replace(/\n{3,}/g, '\n\n').trim();
  }

  private sanitizeBusinessCardRichText(html: string): string {
    if (!html) {
      return '';
    }

    const allowedTags = new Set(['A', 'B', 'BLOCKQUOTE', 'BR', 'CODE', 'DIV', 'EM', 'H1', 'I', 'LI', 'OL', 'P', 'PRE', 'S', 'SPAN', 'STRONG', 'UL']);
    const allowedFontSizes = new Set(['0.85em', '1.2em', '1.45em']);
    const doc = document.implementation.createHTMLDocument('');
    doc.body.innerHTML = html;

    Array.from(doc.body.querySelectorAll('*')).forEach((element) => {
      if (!allowedTags.has(element.tagName)) {
        element.replaceWith(...Array.from(element.childNodes));
        return;
      }

      Array.from(element.attributes).forEach((attribute) => {
        const name = attribute.name.toLowerCase();
        if (name === 'href' && element.tagName === 'A') {
          const href = this.sanitizedBusinessCardLinkUrl(attribute.value);
          if (href) {
            element.setAttribute('href', href);
          } else {
            element.removeAttribute(attribute.name);
          }
          return;
        }

        if (name !== 'style' || element.tagName !== 'SPAN') {
          element.removeAttribute(attribute.name);
          return;
        }

        const fontSize = (element as HTMLElement).style.fontSize;
        if (allowedFontSizes.has(fontSize)) {
          element.setAttribute('style', `font-size: ${fontSize};`);
        } else {
          element.removeAttribute(attribute.name);
        }
      });
    });

    return doc.body.innerHTML.slice(0, 2_000);
  }

  private sanitizedBusinessCardLinkUrl(value: string): string | null {
    const trimmedValue = value.trim();
    if (!trimmedValue) {
      return null;
    }

    try {
      const url = new URL(trimmedValue, window.location.origin);
      return ['http:', 'https:', 'mailto:', 'tel:'].includes(url.protocol) ? trimmedValue : null;
    } catch {
      return null;
    }
  }

  private escapeHtml(value: string): string {
    return value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  private businessCardQrCodesFor(record: BusinessCardRecord): QrCodeRecord[] {
    const records = record.qr_codes?.length ? record.qr_codes : (record.qr_code ? [record.qr_code] : []);
    return records.filter((qrCode, index, all) => all.findIndex((candidate) => candidate.id === qrCode.id) === index);
  }

  private qrCodeHasLayoutOnAnySide(qrCode: QrCodeRecord): boolean {
    const elementId = this.qrElementId(qrCode);
    return Boolean(this.businessCardLayout.front[elementId] || this.businessCardLayout.back[elementId]);
  }

  private async ensureBusinessCardQrImagesForPrint(): Promise<void> {
    const images = { ...this.businessCardQrImages };

    for (const qrCode of this.businessCardQrCodes.filter((record) => this.qrCodeHasLayoutOnAnySide(record))) {
      if (images[qrCode.id]) {
        continue;
      }

      images[qrCode.id] = await this.qrImageDataUrlForRecord(qrCode);
    }

    this.businessCardQrImages = images;
  }

  private previewQrCodeRecord(targetUrl: string, qrStyle: QrStyleId): QrCodeRecord {
    const id = -Date.now();
    return {
      id,
      title: this.businessCardQrForm.controls.title.value || 'Preview QR',
      target_url: targetUrl,
      redirect_path: '',
      track_analytics: false,
      provided_by_page: false,
      user_agent_routes: [],
      scan_count: 0,
      qr_style: qrStyle,
      qr_label_text: this.businessCardQrForm.controls.qrLabelText.value,
      qr_icon_size: this.normalizedQrIconSize(this.businessCardQrForm.controls.qrIconSize.value),
      qr_label_size: this.normalizedQrLabelSize(this.businessCardQrForm.controls.qrLabelSize.value),
      logo_data_url: this.uploadedLogoDataUrl || null,
      qr_image_data_url: '',
      qr_image_base64: '',
      qr_image_content_type: '',
      category_ids: [],
      category_names: this.categoryNamesFromInput(this.businessCardQrForm.controls.categoryNames.value),
      categories: [],
      created_at: new Date().toISOString()
    };
  }

  private syncBusinessCardTrackedAnalyticsControl(): void {
    const control = this.businessCardQrForm.controls.trackAnalytics;

    if (this.authToken) {
      control.enable({ emitEvent: false });
      control.setValue(true, { emitEvent: false });
    } else {
      control.setValue(false, { emitEvent: false });
      control.disable({ emitEvent: false });
    }

    this.applyProvidedByPreferenceToNewQrForms();
  }

  private async checkBusinessCardNfcSupport(): Promise<void> {
    this.isCheckingBusinessCardNfc = true;

    try {
      const [canWrite, canBroadcast, canLock] = await Promise.all([
        this.nfcWriter.canWrite(),
        this.nfcWriter.canBroadcast(),
        this.nfcWriter.canLock()
      ]);
      this.canWriteBusinessCardNfc = canWrite;
      this.canBroadcastBusinessCardNfc = canBroadcast;
      this.canLockBusinessCardNfc = canLock;
      if (!canLock) {
        this.lockBusinessCardNfcAfterWrite = false;
      }
      this.businessCardNfcMessage = this.canWriteBusinessCardNfc
        ? ''
        : 'NFC writing requires Chrome on Android (Web NFC) or the installed mobile app on a device with NFC enabled.';

    } finally {
      this.isCheckingBusinessCardNfc = false;
    }
  }

  private businessCardNfcTargetUrl(): string {
    if (this.selectedNfcQrCode) {
      return this.nfcQrCodeUrl(this.selectedNfcQrCode);
    }

    const selectedSideQrCodes = this.businessCardQrCodesForSelectedSide();
    const qrCode = selectedSideQrCodes.find((record) => record.id > 0)
      || selectedSideQrCodes[0]
      || this.businessCardQrCodes.find((record) => record.id > 0)
      || this.businessCardQrCodes[0];
    if (qrCode) {
      return qrCode.track_analytics && qrCode.redirect_path ? this.redirectUrlFor(qrCode) : qrCode.target_url;
    }

    return this.normalizedNfcUrl(this.businessCardForm.controls.website.value);
  }

  private applyNfcQrCode(record: QrCodeRecord): void {
    const url = this.nfcQrCodeUrl(record);
    this.selectedNfcQrCode = record;
    this.selectedExistingNfcQrCodeId = record.id;
    this.showNfcQrCodeSearch = false;
    this.businessCardForm.controls.website.setValue(url, { emitEvent: false });
    this.businessCardNfcMessage = '';
    this.nfcTagMessage = '';
  }

  private nfcQrCodeUrl(record: QrCodeRecord): string {
    return record.track_analytics && record.redirect_path ? this.redirectUrlFor(record) : record.target_url;
  }

  private normalizedNfcUrl(value: string): string {
    const trimmed = value.trim();
    if (!trimmed) {
      return '';
    }

    return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
  }

  private currentNfcTagPayload(): NfcTagPayload | null {
    const mode = this.businessCardNfcPayloadMode as NfcPayloadMode;
    const targetUrl = mode === 'url' ? this.businessCardNfcTargetUrl() : '';
    const vcard = mode === 'vcard' ? this.businessCardVCard() : '';

    if (mode === 'vcard' && this.businessCardForm.controls.name.invalid) {
      this.businessCardForm.markAllAsTouched();
      this.businessCardNfcMessage = 'Add a full name before writing or saving a contact NFC tag.';
      return null;
    }

    if (mode === 'url' && !targetUrl) {
      this.businessCardNfcMessage = 'Add a website before writing or saving an NFC tag.';
      return null;
    }

    if (mode === 'vcard' && !vcard) {
      this.businessCardNfcMessage = 'Add a name before writing or saving a contact NFC tag.';
      return null;
    }

    const value = this.businessCardForm.getRawValue();
    const title = this.nfcTagForm.controls.title.value.trim()
      || (mode === 'vcard' ? value.name.trim() : targetUrl)
      || 'NFC tag';

    return {
      title,
      payload_mode: mode,
      payload: mode === 'vcard' ? vcard : targetUrl,
      target_url: mode === 'url' ? targetUrl : (value.website.trim() || null),
      contact: {
        name: value.name.trim(),
        jobTitle: value.jobTitle.trim(),
        company: value.company.trim(),
        phone: value.phone.trim(),
        email: value.email.trim(),
        website: value.website.trim(),
        address: value.address.trim(),
        tagline: value.tagline.trim()
      }
    };
  }

  private businessCardVCard(): string {
    const value = this.businessCardForm.getRawValue();
    const name = value.name.trim();
    if (!name) {
      return '';
    }

    const lines = [
      'BEGIN:VCARD',
      'VERSION:3.0',
      `FN:${this.escapeVCardValue(name)}`,
      value.company.trim() ? `ORG:${this.escapeVCardValue(value.company.trim())}` : '',
      value.jobTitle.trim() ? `TITLE:${this.escapeVCardValue(value.jobTitle.trim())}` : '',
      value.phone.trim() ? `TEL:${this.escapeVCardValue(value.phone.trim())}` : '',
      value.email.trim() ? `EMAIL:${this.escapeVCardValue(value.email.trim())}` : '',
      value.website.trim() ? `URL:${this.escapeVCardValue(value.website.trim())}` : '',
      value.address.trim() ? `ADR;TYPE=WORK:;;${this.escapeVCardValue(value.address.trim())}` : '',
      value.tagline.trim() ? `NOTE:${this.escapeVCardValue(value.tagline.trim())}` : '',
      'END:VCARD'
    ];

    return lines.filter(Boolean).join('\n');
  }

  private escapeVCardValue(value: string): string {
    return value
      .replace(/\\/g, '\\\\')
      .replace(/\n/g, '\\n')
      .replace(/,/g, '\\,')
      .replace(/;/g, '\\;');
  }

  private nfcErrorMessage(error: unknown, fallback = 'Unable to write NFC tag. Confirm NFC is enabled and the tag is writable.'): string {
    if (typeof error === 'string') {
      return error;
    }

    if (error && typeof error === 'object' && 'message' in error && typeof error.message === 'string') {
      return error.message;
    }

    return fallback;
  }

  private clampPercent(value: number): number {
    if (!Number.isFinite(value)) {
      return 0;
    }

    return Math.min(92, Math.max(0, Math.round(value * 10) / 10));
  }

  private positionBusinessCardPercent(value: number): number {
    return this.businessCardSnapToGrid ? this.snapBusinessCardPercent(value) : this.clampPercent(value);
  }

  private snapBusinessCardPercent(value: number): number {
    const gridSize = this.normalizedBusinessCardGridSize(this.businessCardGridSize);
    return this.clampPercent(Math.round(value / gridSize) * gridSize);
  }

  private normalizedBusinessCardGridSize(value: number | string | null | undefined): number {
    const size = Number(value);
    if (!Number.isFinite(size)) {
      return DEFAULT_BUSINESS_CARD_GRID_SIZE;
    }

    return Math.min(10, Math.max(1, Math.round(size)));
  }

  private clampBackgroundFade(value: number): number {
    if (!Number.isFinite(value)) {
      return 85;
    }

    return Math.min(100, Math.max(0, Math.round(value)));
  }

  private businessCardQrCodeSizeForElement(side: BusinessCardSide, elementId: string): number {
    const position = this.businessCardLayout[side][elementId];
    return this.normalizedBusinessCardQrCodeSize(position?.size ?? 22);
  }

  private normalizedBusinessCardQrCodeSize(value: number | string | null | undefined): number {
    const size = Number(value);
    if (!Number.isFinite(size)) {
      return 22;
    }

    return Math.min(42, Math.max(8, Math.round(size)));
  }

  private normalizedStickerSize(value: number | string): StickerSizeInches {
    if (value === '') {
      return 1;
    }

    const size = Number(value);
    if (!Number.isFinite(size)) {
      return 1;
    }

    return Math.min(2, Math.max(0.5, Math.round(size * 100) / 100));
  }

  private normalizedQrIconSize(value: number | string | null | undefined): number {
    const size = Number(value);
    if (!Number.isFinite(size)) {
      return 28;
    }

    return Math.min(32, Math.max(12, Math.round(size)));
  }

  private normalizedQrLabelSize(value: number | string | null | undefined): number {
    const size = Number(value);
    if (!Number.isFinite(size)) {
      return 100;
    }

    return Math.min(100, Math.max(60, Math.round(size)));
  }

  private clampInteger(value: number | string | null | undefined, minimum: number, maximum: number): number {
    const size = Number(value);
    if (!Number.isFinite(size)) {
      return minimum;
    }

    return Math.min(maximum, Math.max(minimum, Math.round(size)));
  }

  private normalizeHexColor(value: unknown, fallback = '#164c63'): string {
    return typeof value === 'string' && /^#[0-9a-fA-F]{6}$/.test(value) ? value : fallback;
  }

  private businessCardDefaultTextColor(): string {
    return DEFAULT_BUSINESS_CARD_TEXT_COLOR;
  }

  private readableTextColorForHex(value: string): '#111827' | '#f9fafb' {
    const color = this.normalizeHexColor(value);
    const red = parseInt(color.slice(1, 3), 16);
    const green = parseInt(color.slice(3, 5), 16);
    const blue = parseInt(color.slice(5, 7), 16);
    const luminance = (0.2126 * red + 0.7152 * green + 0.0722 * blue) / 255;

    return luminance > 0.58 ? '#111827' : '#f9fafb';
  }

  private normalizeBusinessCardBackgroundStyle(value: unknown, fallback: BusinessCardBackgroundStyleId = 'linear'): BusinessCardBackgroundStyleId {
    const style = this.businessCardBackgroundStyles.find((option) => option.id === value);
    return style ? style.id : fallback;
  }

  private syncLimitNotice(meta?: AnalyticsLimitMeta): void {
    this.qrCodeLimitBypassed = Boolean(meta?.analytics_qr_code_limit_bypassed);
    this.urlResponseValidationBypassAllowed = Boolean(meta?.url_response_validation_bypass_allowed);
    this.limitNoticeMessage = meta?.analytics_qr_code_limit_reached && !this.qrCodeLimitBypassed
      ? meta.analytics_qr_code_limit_message || ''
      : '';
  }

  private syncLimitNoticeFromList(): void {
    if (this.qrCodeLimitBypassed) {
      this.limitNoticeMessage = '';
      return;
    }

    this.limitNoticeMessage = this.qrCodes.length >= 5
      ? 'This service allows 5 tracked QR codes per user on the free plan. Subscribe for $10/month through Stripe to unlock unlimited use.'
      : '';
  }

  private syncBusinessCardLimitNotice(meta?: BusinessCardLimitMeta): void {
    this.businessCardLimitBypassed = Boolean(meta?.business_card_limit_bypassed);
    this.businessCardLimitMessage = meta?.business_card_limit_reached && !this.businessCardLimitBypassed
      ? meta.business_card_limit_message || ''
      : '';
  }

  private syncBusinessCardLimitNoticeFromList(): void {
    if (this.businessCardLimitBypassed) {
      this.businessCardLimitMessage = '';
      return;
    }

    this.businessCardLimitMessage = this.businessCards.length >= 5
      ? 'You have 5 saved business cards. Delete a card or edit an existing card before creating another.'
      : '';
  }

  private businessCardUsageLimitReached(): boolean {
    return !this.generatedBusinessCard && !this.businessCardLimitBypassed && (
      Boolean(this.businessCardLimitMessage) || this.businessCards.length >= 5
    );
  }

  private syncNfcTagLimitNotice(meta?: NfcTagLimitMeta): void {
    if (!meta) {
      this.syncNfcTagLimitNoticeFromList();
      return;
    }

    this.nfcTagLimitBypassed = Boolean(meta?.nfc_tag_limit_bypassed);
    this.nfcTagLimitMessage = meta?.nfc_tag_limit_reached && !this.nfcTagLimitBypassed
      ? meta.nfc_tag_limit_message || ''
      : '';
  }

  private syncNfcTagLimitNoticeFromList(): void {
    if (this.nfcTagLimitBypassed) {
      this.nfcTagLimitMessage = '';
      return;
    }

    this.nfcTagLimitMessage = this.nfcTags.length >= 5
      ? 'You have 5 saved NFC tags. Subscribe for $10/month through Stripe to unlock unlimited use.'
      : '';
  }

  private nfcTagUsageLimitReached(): boolean {
    return !this.selectedNfcTag && !this.nfcTagLimitBypassed && (
      Boolean(this.nfcTagLimitMessage) || this.nfcTags.length >= 5
    );
  }

  private resetFormForReuse(): void {
    this.form.reset(this.qrFormState.state(this.qrFormOptions(), {
      qrLabelText: this.activeQrLabelText,
      qrIconSize: String(this.activeQrIconSize),
      qrLabelSize: String(this.activeQrLabelSize),
      qrStyle: 'classic'
    }), { emitEvent: false });
    this.uploadedLogoDataUrl = '';

    this.qrCodeForm?.clearLogoInput();

    this.syncTrackedAnalyticsControl();
  }

  private applyProvidedByPreferenceToNewQrForms(): void {
    const providedByPage = this.defaultProvidedByPage();

    if (this.form.controls.providedByPage.pristine) {
      this.form.controls.providedByPage.setValue(providedByPage, { emitEvent: false });
    }

    if (this.businessCardQrForm.controls.providedByPage.pristine) {
      this.businessCardQrForm.controls.providedByPage.setValue(providedByPage, { emitEvent: false });
    }
  }

  private defaultProvidedByPage(): boolean {
    return Boolean(this.authToken) && !this.currentUserSkipProvidedByPage;
  }

  private qrFormOptions(): { authToken: boolean; skipProvidedByPage: boolean } {
    return {
      authToken: Boolean(this.authToken),
      skipProvidedByPage: this.currentUserSkipProvidedByPage
    };
  }

  private closeEditModal(): void {
    this.isEditModalOpen = false;
    this.editingRecord = null;
    this.editLogoDataUrl = '';
    this.errorMessage = '';
    this.editForm.reset(this.qrFormState.state(this.qrFormOptions(), { trackAnalytics: false }), { emitEvent: false });
    this.editForm.controls.trackAnalytics.disable({ emitEvent: false });

    if (this.editLogoInput?.nativeElement) {
      this.editLogoInput.nativeElement.value = '';
    }
  }

  private syncTrackedAnalyticsControl(): void {
    const control = this.form.controls.trackAnalytics;

    if (this.authToken) {
      control.enable({ emitEvent: false });
      control.setValue(true, { emitEvent: false });
    } else {
      control.setValue(false, { emitEvent: false });
      control.disable({ emitEvent: false });
    }

    this.applyProvidedByPreferenceToNewQrForms();
  }

  private syncApiTokenExpiresControl(): void {
    const expiresAtControl = this.apiTokenForm.controls.expiresAt;

    if (this.apiTokenForm.controls.neverExpires.value) {
      expiresAtControl.disable({ emitEvent: false });
    } else {
      expiresAtControl.enable({ emitEvent: false });
    }
  }

  private syncLegalPageFromLocation(): void {
    const slug = window.location.pathname.replace(/^\/+/, '').split('/')[0];
    this.selectedLegalPage = this.legalPages.find((page) => page.slug === slug) || null;
  }

  private authHeaders(): Record<string, string> {
    return this.session.authHeaders();
  }

  private apiV1Url(path: string): string {
    return this.apiUrls.v1(path);
  }

  private apiUrl(path: string): string {
    return this.apiUrls.url(path);
  }

  private availableOAuthProviders(providers: OAuthProviderRecord[]): Array<{ id: OAuthProvider; label: string }> {
    const configuredIds = new Set(providers.map((provider) => provider.id));
    return DEFAULT_OAUTH_PROVIDERS.filter((provider) => configuredIds.has(provider.id));
  }

  private apiBaseUrl(): string {
    return this.apiUrls.baseUrl();
  }

  private redirectUrlFor(record: QrCodeRecord): string {
    return new URL(record.redirect_path, this.redirectBaseUrl()).toString();
  }

  private redirectBaseUrl(): string {
    return this.apiBaseUrl();
  }

  private async qrStickerImageDataUrl(
    value: string,
    styleId: QrStyleId,
    qrLabelText: string,
    qrIconSize: number,
    qrLabelSize: number,
    stickerSize: StickerSizeInches,
    logoImage = this.logoImage
  ): Promise<string> {
    const style = this.selectedQrStyle(styleId);
    return this.qrRender.qrStickerImageDataUrl(
      value,
      style,
      qrLabelText,
      qrIconSize,
      qrLabelSize,
      stickerSize,
      logoImage
    );
  }

  private selectedQrStyle(styleId = this.form.controls.qrStyle.value): QrStyle {
    return this.qrStyles.find((style) => style.id === styleId) || this.qrStyles[0];
  }

  private async renderQrPreview(
    value: string,
    styleId = this.form.controls.qrStyle.value,
    qrLabelText = this.activeQrLabelText,
    qrIconSize = this.activeQrIconSize,
    qrLabelSize = this.activeQrLabelSize
  ): Promise<void> {
    const canvas = this.qrPreview?.canvas;
    if (!canvas) {
      return;
    }

    const style = this.selectedQrStyle(styleId);
    this.generatedImageUrl = await this.qrRender.renderPreviewToCanvas(canvas, value, style, {
      qrLabelText,
      qrIconSize,
      qrLabelSize,
      logoImage: this.logoImage
    });
  }

  private async renderQrRecordPreview(record: QrCodeRecord): Promise<void> {
    if (record.qr_image_data_url) {
      await this.renderQrImageDataUrlToCanvas(record.qr_image_data_url);
      this.generatedImageUrl = record.qr_image_data_url;
      return;
    }

    await this.renderQrPreview(
      this.qrValueForRecord(record),
      record.qr_style,
      record.qr_label_text || '',
      record.qr_icon_size,
      record.qr_label_size
    );
  }

  private async renderQrImageDataUrlToCanvas(dataUrl: string): Promise<void> {
    const canvas = this.qrPreview?.canvas;
    const context = canvas?.getContext('2d');
    if (!canvas || !context) {
      return;
    }

    const image = await this.loadImage(dataUrl);
    const width = image.naturalWidth || image.width;
    const height = image.naturalHeight || image.height;
    const size = Math.max(width, height);
    const x = (size - width) / 2;
    const y = (size - height) / 2;

    canvas.width = size;
    canvas.height = size;
    context.clearRect(0, 0, canvas.width, canvas.height);
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, size, size);
    context.drawImage(image, x, y, width, height);
  }

  private async qrImageDataUrlForRecord(record: QrCodeRecord): Promise<string> {
    // Business-card QR elements are sized by their layout box. Re-render locally so
    // saved API images with larger quiet-zone padding do not visually shrink in place.
    return this.qrRender.qrImageDataUrl(
      this.qrValueForRecord(record),
      this.selectedQrStyle(record.qr_style),
      {
        qrLabelText: record.qr_label_text || '',
        qrIconSize: record.qr_icon_size,
        qrLabelSize: record.qr_label_size,
        logoImage: await this.logoImageForQrCode(record)
      }
    );
  }

  private qrValueForRecord(record: QrCodeRecord): string {
    return record.track_analytics && record.redirect_path ? this.redirectUrlFor(record) : record.target_url;
  }

  private async setActiveLogo(dataUrl: string | null): Promise<void> {
    this.logoImage = dataUrl ? await this.loadImage(dataUrl) : null;
  }

  private async logoImageForQrCode(qrCode: QrCodeRecord): Promise<HTMLImageElement | null> {
    if (!qrCode.logo_data_url) {
      return null;
    }

    return this.loadImage(qrCode.logo_data_url);
  }

  private loadImage(src: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const image = new Image();
      image.onload = () => resolve(image);
      image.onerror = reject;
      image.src = src;
    });
  }

  private async compressLogoDataUrl(file: File): Promise<string> {
    const sourceDataUrl = await this.readFileAsDataUrl(file);
    const image = await this.loadImage(sourceDataUrl);

    for (const maxDimension of LOGO_RESIZE_STEPS) {
      const resizedDataUrl = this.resizeImageToDataUrl(image, maxDimension);
      if (resizedDataUrl.length <= MAX_STORED_LOGO_DATA_URL_LENGTH) {
        return resizedDataUrl;
      }
    }

    throw new Error('Logo is too large to store.');
  }

  private resizeImageToDataUrl(image: HTMLImageElement, maxDimension: number): string {
    const scale = Math.min(1, maxDimension / Math.max(image.naturalWidth, image.naturalHeight));
    const width = Math.max(1, Math.round(image.naturalWidth * scale));
    const height = Math.max(1, Math.round(image.naturalHeight * scale));
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');

    if (!context) {
      throw new Error('Canvas is unavailable.');
    }

    canvas.width = width;
    canvas.height = height;
    context.drawImage(image, 0, 0, width, height);

    return canvas.toDataURL('image/png');
  }

  private readFileAsDataUrl(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result || ''));
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(file);
    });
  }
}
